import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { GEO5_MODULES, GEO5_PACKAGES, GEO5_MODULES_LISTE, GEO5_COVERAGE } from "../kb/geo5Modules";
import { trouverMeilleurPackage } from "../utils/regles";
import { CIVISOFT_CONTACT } from "../constants";
import CalculateurDeuxMurs from "./CalculateurDeuxMurs";
import { USE_BACKEND_RANKING } from "../config";

const formatSolutionId = (id) => id ? id.replaceAll("_", " ") : "";

function AlerteModulesManquants({ solutionModules, profilUtilisateur }) {
  const { t } = useTranslation();
  if (!profilUtilisateur?.estUtilisateurGeo5) return null;
  const posedes = profilUtilisateur.modulesGeo5 || [];

  // Retrouver les IDs correspondants dans GEO5_MODULES_LISTE
  const toId = (nomModule) => {
    const nom = nomModule.toLowerCase().replace(/\s+/g,"_").replace(/é/g,"e").replace(/è/g,"e");
    for (const cat of GEO5_MODULES_LISTE) {
      const m = cat.modules.find(m => m.id.includes(nom) || nom.includes(m.id.split("_")[0]));
      if (m) return m.id;
    }
    // Matching approximatif
    const MAP = {
      "murs cantilever":"murs_cantilever", "cantilever":"murs_cantilever",
      "stabilite des pentes":"stabilite_pentes", "stabilité des pentes":"stabilite_pentes", "pentes":"stabilite_pentes",
      "pentes clouees":"pentes_clouees", "pentes clouées":"pentes_clouees", "clouees":"pentes_clouees",
      "verification ecrans":"verification_ecrans", "vérification écrans":"verification_ecrans",
      "conception ecrans":"conception_ecrans",
      "semelles":"semelles", "pieux":"pieux", "tassements":"tassements",
      "culees":"culees", "culées":"culees",
    };
    for (const [key, id] of Object.entries(MAP)) {
      if (nom.includes(key)) return id;
    }
    return null;
  };

  const manquants = solutionModules
    .map(nomMod => ({ nom:nomMod, id: toId(nomMod) }))
    .filter(m => m.id && !posedes.includes(m.id));

  if (manquants.length === 0) return (
    <div style={{ background:"#F0FDF4", border:"1.5px solid #86EFAC", borderRadius:10, padding:"12px 16px", marginTop:12 }}>
      <span style={{ fontSize:13, fontWeight:700, color:"#166534" }}>{t("solution_page.modules_ok")}</span>
    </div>
  );

  return (
    <div style={{ background:"#FFF7ED", border:"1.5px solid #FED7AA", borderRadius:12, padding:"16px 18px", marginTop:12 }}>
      <div style={{ fontSize:13, fontWeight:800, color:"#92400E", marginBottom:8 }}>
        {t("solution_page.modules_manquants_titre")}
      </div>
      <div style={{ marginBottom:12 }}>
        {manquants.map(m => {
          const info = GEO5_MODULES_LISTE.flatMap(c => c.modules).find(mod => mod.id === m.id);
          return (
            <div key={m.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 0", borderBottom:"1px solid #FED7AA" }}>
              <span>{info?.icone || "📦"}</span>
              <div>
                <span style={{ fontSize:13, fontWeight:700, color:"#78350F" }}>{m.nom}</span>
                <span style={{ fontSize:11, color:"#92400E", marginLeft:8 }}>{t("solution_page.modules_manquants_note")}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize:12, color:"#92400E", lineHeight:1.7, background:"#FEF3C7", borderRadius:8, padding:"10px 14px" }}>
        <strong>{t("solution_page.modules_manquants_why")}</strong><br/>
        {t("solution_page.modules_manquants_desc")}<br/><br/>
        <strong>{t("solution_page.modules_contact")}</strong><br/>
        Tél : <strong>{CIVISOFT_CONTACT.tel}</strong><br/>
        Email : <strong>{CIVISOFT_CONTACT.email}</strong><br/>
        Web : <strong>{CIVISOFT_CONTACT.web}</strong>
      </div>
    </div>
  );
}


// =============================================================================
// PAGE SOLUTION — Résultats de l'analyse + navigation vers Accueil / Agent IA
// =============================================================================
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const buildDiagnosticPayload = (donneesProjet) => ({
  country:          "FR",
  language:         "fr",
  typeOuvrage:      donneesProjet.typeOuvrage || "",
  hauteur:          parseFloat(donneesProjet.hauteur) || 0,
  rdt:              parseFloat(donneesProjet.rdt) || 200,
  typeSol:          donneesProjet.couches?.[0]?.nature || donneesProjet.typeSol || "",
  couches:          (donneesProjet.couches || []).map(c => ({
    nature:          c.nature || "",
    profondeur_toit: parseFloat(c.profondeur_toit) || 0,
    profondeur_base: parseFloat(c.profondeur_base) || 3,
    gamma:           parseFloat(c.gamma) || 18,
    cohesion:        parseFloat(c.cohesion) || 0,
    phi:             parseFloat(c.phi) || 25,
  })),
  nappe:            donneesProjet.nappe || "absente",
  nappeProf:        parseFloat(donneesProjet.nappeProf) || 0,
  zoneSismique:     donneesProjet.zoneSismique || "1",
  espaceDisponible: donneesProjet.espaceDisponible ?? "",
  milieuUrbain:     donneesProjet.zoneUsage === "Milieu urbain (contrainte d'emprise)",
  budget:           donneesProjet.budget || "",
  delai:            donneesProjet.delai || "normal",
  essais:                  [...new Set(donneesProjet.essais || [])],
  E_module:                parseFloat(donneesProjet.E_module) || 0,
  pl_etoile:               parseFloat(donneesProjet.pl_etoile) || 0,
  batimentVoisin:           donneesProjet.batimentVoisin || false,
  batimentVoisinDistance:   parseFloat(donneesProjet.batimentVoisinDistance) || 0,
  profondeur_encastrement:  parseFloat(donneesProjet.profondeur_encastrement) || 0.8,
  servitudeTrefonds:        donneesProjet.servitudeTrefonds    || "inconnue",
  avoisinantsSensibles:     Boolean(donneesProjet.avoisinantsSensibles),
});


const getModulesGEO5 = (solutionId, diagnosticBackend, solutionFrontend) => {
  const modulesBackend = diagnosticBackend?.modules_geo5?.par_solution_eligible?.[solutionId];
  if (modulesBackend?.length > 0) return modulesBackend;
  if (solutionFrontend?.modules_geo5?.length > 0) return solutionFrontend.modules_geo5;
  return [];
};



const NIVEAU_CONFIG = {
  "TRÈS_ADAPTÉE":          { label: "Très adaptée",          color: "#16A34A", bg: "#F0FDF4" },
  "ADAPTÉE":               { label: "Adaptée",               color: "#0284C7", bg: "#F0F9FF" },
  "ADAPTÉE_AVEC_RÉSERVES": { label: "Adaptée avec réserves", color: "#D97706", bg: "#FFFBEB" },
  "PEU_ADAPTÉE":           { label: "Peu adaptée",           color: "#DC2626", bg: "#FEF2F2" },
  "COMPLÉMENTAIRE":        { label: "Complémentaire",        color: "#64748B", bg: "#F8FAFC" },
};

const resolveTexte = (item) => {
  if (typeof item === "string") return item;
  return item?.alerte || item?.message || item?.justification || item?.raison || item?.id || JSON.stringify(item);
};

const limiterListe = (liste, max = 5) => ({
  affichees: liste.slice(0, max),
  restantes: Math.max(0, liste.length - max),
});



// Correspondance typeOuvrage frontend → type_ouvrage backend
const MAP_TYPE_OUVRAGE = {
  "soutenement_remblai":    "mur",
  "soutenement_deblai":     "fouille",
  "talus_stabilisation":    "talus",
  "fondation_batiment":     "batiment",
  "infrastructure_lineaire":"route",
  "ouvrage_art":            "pont",
  "amelioration_sol":       "amelioration",
};

function PageSolution({ donneesProjet, profilUtilisateur, onRetour, onAgent, onSolutions, onModifier, projetModifie, onClientInfoUpdate, userId, onDiagnosticBackend, onRapport, modeConsultationProjet = false, onProjetClone = null, onSolutionRetenue = null }) {
  const { t } = useTranslation();
  const [analysing, setAnalysing]         = useState(true);
  const [showCalc, setShowCalc]           = useState(false);
  const [alertesProjet, setAlertesProjet] = useState([]);
  const [alertesEN2,   setAlertesEN2]     = useState([]);
  const [missionsEN2,  setMissionsEN2]    = useState([]);
  const [preDimData,   setPreDimData]     = useState(null);
  const [modeSécurité, setModeSécurité]   = useState(false);
  const [showSave, setShowSave]           = useState(false);
  const [showModifier, setShowModifier]   = useState(false);

  const [verif3Visible, setVerif3Visible]   = useState(false);
  const [nomProjet, setNomProjet]         = useState(() => {
    const ci = donneesProjet?.clientInfo;
    if (ci?.nomEntreprise && ci?.codeRef) return `${ci.nomEntreprise} — ${ci.codeRef}`;
    if (ci?.nomEntreprise) return ci.nomEntreprise;
    return "";
  });
  const [saveMsg, setSaveMsg]             = useState("");
  const [showSaveAsModal, setShowSaveAsModal]           = useState(false);
  const [saveAsNomEntreprise, setSaveAsNomEntreprise]   = useState('');
  const [saveAsCodeRef, setSaveAsCodeRef]               = useState('');
  const [diagnosticBackend, setDiagnosticBackend] = useState(null);
  const [diagnosticBackendFailed, setDiagnosticBackendFailed] = useState(false);
  const [essaisVisible, setEssaisVisible] = useState(false);
  const [showAutres,   setShowAutres]     = useState(false);
  const [solutionRetenue, setSolutionRetenue] = useState(() => donneesProjet?.solution_retenue || null);

  const GV = {
    vert:"#2D7A2D", vertClair:"#00A651", vertLight:"#E8F5E9",
    vertMid:"#4CAF50", vertDark:"#1B5E20", blanc:"#FFFFFF",
    gris:"#F5F5F5", grisMid:"#757575", dark:"#1A1A1A", border:"#E0E0E0",
  };

  // Analyse IA : simulation 2.8s + appel backend /analyser pour les alertes
  useEffect(() => {
    if (
      modeConsultationProjet &&
      Array.isArray(donneesProjet?.solutions) &&
      donneesProjet.solutions.length > 0
    ) {
      setAnalysing(false);
      fetch(`${API_URL}/diagnostic`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildDiagnosticPayload(donneesProjet)),
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) {
            setDiagnosticBackend(data);
            if (onDiagnosticBackend) onDiagnosticBackend(data);
          }
        })
        .catch(() => {});
      return;
    }

    const t = setTimeout(() => setAnalysing(false), 2800);

    if (donneesProjet) {
      const backendData = {
        type_ouvrage:      MAP_TYPE_OUVRAGE[donneesProjet.typeOuvrage] || donneesProjet.typeOuvrage || "inconnu",
        fouille_profondeur: parseFloat(donneesProjet.hauteur) || 0,
        zone_sismique:      donneesProjet.zoneSismique || null,
        nappe_phreatique:   ["moderee","haute","saisonniere"].includes(donneesProjet.nappe),
        nappe_profondeur:   parseFloat(donneesProjet.nappeProf) || 99,
        description_projet: donneesProjet.description || donneesProjet.zoneUsage || "",
        nb_niveaux:         parseInt(donneesProjet.nbNiveaux) || 0,
        essais_disponibles: donneesProjet.essais || [],
        couches: (donneesProjet.couches || []).map(c => ({
          nature:    c.nature || c.typeSol || "inconnu",
          cohesion:  parseFloat(c.cohesion) || 0,
          phi:       parseFloat(c.frottement) || parseFloat(c.phi) || 30,
          N_spt:     parseInt(c.N_spt) || null,
        })),
        // Contexte urbain / soutènement
        espaceDisponible:          donneesProjet.espaceDisponible || (donneesProjet.zoneUsage === "Milieu urbain (contrainte d'emprise)" ? "limite" : null),
        milieu_urbain:             donneesProjet.zoneUsage === "Milieu urbain (contrainte d'emprise)" || donneesProjet.milieuUrbain || false,
        batimentVoisin:            donneesProjet.batimentVoisin || false,
        batiment_voisin_distance:  parseFloat(donneesProjet.batimentVoisinDistance) || null,
        talon_max:                 parseFloat(donneesProjet.talonMax) || null,
        inclinaison_talus:         parseFloat(donneesProjet.inclinaisonTalus) || null,
        nb_couches_complexes:      (donneesProjet.couches || []).length > 2 ? (donneesProjet.couches || []).length : null,
        profondeur_encastrement:   parseFloat(donneesProjet.profondeur_encastrement) || null,
        regle_fs018:               ["calcaire_sain","calcaire_altere","roche"].includes(
                                     (donneesProjet.couches||[]).slice().reverse().find(c => c.nature && !["remblai","terre_vegetale"].includes(c.nature))?.nature
                                   ) || false,
        pl_etoile:                 parseFloat(donneesProjet.pl_etoile) || null,
        E_module_MPa:              parseFloat(donneesProjet.E_module) || null,
        qc_cpt:                    parseFloat(donneesProjet.qc_cpt) || null,
        source_rdt:                donneesProjet.essais?.includes("pressiometre") ? "pressiometre"
                                   : donneesProjet.essais?.includes("cpt") ? "cpt"
                                   : "estimation",
      };

      fetch(`${API_URL}/analyser`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(backendData),
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.alertes_projet) setAlertesProjet(data.alertes_projet);
          if (data?.mode_securite)  setModeSécurité(true);
        })
        .catch(() => {}); // Silently fail — les alertes sont un bonus
    }

    return () => clearTimeout(t);
  }, []);

  // alertesEN2 : vide en mode BACKEND (bloc appelEN1997_2 conditionné FALLBACK — Phase 8D-B1)
  // En mode FALLBACK uniquement → alertesEN2 contient les alertes /analyser/en1997-2
  // alertes labo : source backend iso17892_alertes (N2.4 — suppression doublon frontend)
  const toutesAlertes = useMemo(
    () => [
      ...alertesProjet,
      ...alertesEN2,
      ...(diagnosticBackend?.essais?.iso17892_alertes || []),
    ],
    [alertesProjet, alertesEN2, diagnosticBackend]
  );



  const [solutions, setSolutions] = useState(() => {
    // EN-PREP-1a.1 — backend = sole source. getSolutions() retiré.
    // modeConsultationProjet : projets sauvegardés conservés (source : App.js).
    if (
      modeConsultationProjet &&
      Array.isArray(donneesProjet?.solutions) &&
      donneesProjet.solutions.length > 0
    ) {
      return donneesProjet.solutions;
    }
    return [];
  });

  // ── Phase 8C — Hiérarchie source de vérité ──
  const backendClassementDisponible =
    diagnosticBackend?.adequation?.classement?.length > 0;

  const sourceMode =
    USE_BACKEND_RANKING && backendClassementDisponible
      ? "BACKEND"
      : "FALLBACK";

  // EN-PREP-1a.2 — garde-fou chargement (pas de flash "indisponible" avant la 1ère réponse)
  // diagnosticAttempted = vrai dès que /diagnostic a terminé (succès ou échec)
  // Dérivé des états existants : aucun nouveau useState nécessaire.
  const diagnosticAttempted = diagnosticBackend !== null || diagnosticBackendFailed === true;
  // showFallbackMinimal : FALLBACK confirmé APRÈS tentative backend (pas pendant le spinner analysing)
  const showFallbackMinimal  = sourceMode === "FALLBACK" && diagnosticAttempted;

  const getLabelNiveau = (solution) => {
    const id = solution?.id || solution?.solution_id || solution?.type;
    const niveau =
      solution?.niveau ||
      solution?.niveau_adequation ||
      diagnosticBackend?.adequation?.solutions?.[id]?.niveau;
    return NIVEAU_CONFIG[niveau]?.label || "Adéquation qualitative";
  };

  // Enrichit une solution classée avec les données détaillées du backend
  const enrichirDepuisBackend = (item) => {
    const id = item.id;
    const solData = diagnosticBackend?.adequation?.solutions?.[id] || {};
    return {
      id,
      rang: item.rang,
      niveau: item.niveau || solData.niveau,
      nom: solData.nom || formatSolutionId(id),
      avantages: solData.motifs_positifs || [],
      inconvenients: solData.avertissements || [],
      motifs_positifs: solData.motifs_positifs || [],
      motifs_negatifs: solData.motifs_negatifs || [],
      avertissements: solData.avertissements || [],
      modules_geo5: diagnosticBackend?.modules_geo5?.par_solution_eligible?.[id] || [],
      normes: diagnosticBackend?.normes?.applicables || [],
      score: null,
      icone: "🧠",
      statut: "classee",
    };
  };

  let displayBest, displayOthers, displayExcluded, displayModules;

  if (sourceMode === "BACKEND") {
    const classement = diagnosticBackend.adequation.classement;
    displayBest = classement[0] ? enrichirDepuisBackend(classement[0]) : null;
    displayOthers = classement.slice(1).map(enrichirDepuisBackend);
    displayExcluded = Object.entries(diagnosticBackend?.eligibilite?.exclues || {})
      .map(([id, data]) => ({
        id,
        nom: data?.nom,
        regles: Array.isArray(data?.regles) ? data.regles : typeof data === "string" ? [data] : Object.keys(data || {}),
        motifs: data?.motifs || [],
        regles_resolues: data?.regles_resolues || [],
      }));
    displayModules = diagnosticBackend?.modules_geo5?.par_solution_eligible || {};
  } else {
    displayBest     = solutions[0] || null;
    displayOthers   = solutions.slice(1);
    displayExcluded = [];
    displayModules  = {};
  }

  useEffect(() => {
    // EN-PREP-1a.1 — backend = sole source. Plus de getSolutions() ici.
    // solutions reste [] jusqu'à réponse backend (propagée via FIX-RANK.b onSolutions).
    setSolutions([]);

    if (!donneesProjet) return;

    // Appel pré-dimensionnement — fondation_batiment uniquement
    if (donneesProjet.typeOuvrage === "fondation_batiment") {
      const coucheFondation = (donneesProjet.couches || []).slice(-1)[0] || {};
      const appelPreDim = async () => {
        try {
          const res = await fetch(`${API_URL}/calculs/pre-dimensionnement`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              B: 1.5,
              D: parseFloat(donneesProjet.profondeur_encastrement) || 1.0,
              c: parseFloat(coucheFondation.cohesion) || 10,
              phi: parseFloat(coucheFondation.frottement || coucheFondation.phi) || 25,
              gamma: parseFloat(coucheFondation.gamma) || 18,
              Eoed: parseFloat(donneesProjet.E_module) || 10,
              E_sol: parseFloat(donneesProjet.E_module) || 15,
              pl_etoile: parseFloat(donneesProjet.pl_etoile) || null,
              charge_service: parseFloat(donneesProjet.rdt) || 200,
              charge_ELU: (parseFloat(donneesProjet.rdt) || 200) * 1.35,
              type_sol: donneesProjet.typeSol || "argile",
              zone_sismique: donneesProjet.zoneSismique || "Z2",
              H_argile: parseFloat(coucheFondation.epaisseur || coucheFondation.profondeur_base) || 3.0,
              type_structure: "beton_arme",
            }),
          });
          if (!res.ok) return;
          setPreDimData(await res.json());
        } catch {
          // Pré-dimensionnement est indicatif — silently fail
        }
      };
      appelPreDim();
    }

    const runDiagnostic = async () => {
      setDiagnosticBackendFailed(false);
      try {
        const payload = buildDiagnosticPayload(donneesProjet);
        const res = await fetch(`${API_URL}/diagnostic`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          setDiagnosticBackend(null); onDiagnosticBackend?.(null);
          setDiagnosticBackendFailed(true);
          return;
        }
        const diag = await res.json();
        setDiagnosticBackend(diag);
        onDiagnosticBackend?.(diag);
        setDiagnosticBackendFailed(false);
      } catch {
        setDiagnosticBackend(null);
        onDiagnosticBackend?.(null);
        setDiagnosticBackendFailed(true);
      }
    };
    runDiagnostic();

  }, [donneesProjet]); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══ LEGACY FALLBACK — Phase 8D-B1bis ═══
  // Appel /analyser/en1997-2 — UNIQUEMENT si backend absent ou réellement échoué
  // Conditions strictes : backendDisponible → skip ; USE_BACKEND_RANKING et non échoué → skip
  useEffect(() => {
    if (!donneesProjet) return;
    const backendDisponible = diagnosticBackend?.adequation?.classement?.length > 0;
    const fallbackAutorise = !USE_BACKEND_RANKING || diagnosticBackendFailed === true;
    if (backendDisponible) return;
    if (!fallbackAutorise) return;

    // EN-PREP-1a.1 — plus de getSolutions() legacy ici.
    const controller = new AbortController();

    const appelEN1997_2 = async () => {
      try {
        const res = await fetch(`${API_URL}/analyser/en1997-2`, {
          signal: controller.signal,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            essais: donneesProjet.essais || [],
            // EN-PREP-1a.1 — type sol depuis backend, fallback saisie
            type_sol: diagnosticBackend?.faits?.sol_critique || donneesProjet?.typeSol || "",
            nappe: ["moderee","haute","saisonniere"].includes(donneesProjet.nappe || ""),
            type_ouvrage: donneesProjet.typeOuvrage || "",
            solutions_eligibles: diagnosticBackend?.eligibilite?.eligibles || [],
          }),
        });
        if (!res.ok) return;
        const resultat = await res.json();
        // ═══ LEGACY FALLBACK — Phase 8B ═══
        // Source de vérité : backend /diagnostic
        // Conservé pour fallback frontend uniquement
        if (resultat.ajustements) {
          setSolutions(prev =>
            prev.map(sol => ({
              ...sol,
              score: Math.min(100, Math.max(0, sol.score + (resultat.ajustements[sol.id] || 0))),
            })).sort((a, b) => b.score - a.score)
          );
        }
        if (resultat.alertes?.length)              setAlertesEN2(resultat.alertes);
        if (resultat.missions_recommandees?.length) setMissionsEN2(resultat.missions_recommandees);
      } catch (err) {
        if (err?.name === "AbortError") return;
        // EN 1997-2 ajustements sont additifs — silently fail
      }
    };

    appelEN1997_2();
    return () => controller.abort();
  }, [donneesProjet, diagnosticBackend, diagnosticBackendFailed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remonter les solutions ET alertes vers App.js → TabCoPilote (legacy fallback)
  useEffect(() => {
    if (solutions.length > 0 && onSolutions && !backendClassementDisponible) {
      onSolutions(solutions, toutesAlertes, modeSécurité);
    }
  }, [solutions.length, toutesAlertes.length, modeSécurité]); // eslint-disable-line

  // FIX-RANK.b — Propagation solutions backend vers Co-Pilote + Rapport PDF
  // Priorité sur le legacy : quand le classement backend arrive, App.js reçoit les vraies solutions.
  useEffect(() => {
    if (!backendClassementDisponible || !onSolutions) return;
    const backendSols = diagnosticBackend.adequation.classement.map(enrichirDepuisBackend);
    if (backendSols.length > 0) onSolutions(backendSols, toutesAlertes, modeSécurité);
  }, [backendClassementDisponible]); // eslint-disable-line

  // ── Sauvegarde localStorage ────────────────────────────────────────────
  const sauvegarderProjet = () => {
    const now = new Date();
    let clientInfo = donneesProjet?.clientInfo || null;

    // Si modification → incrémenter variant + enregistrer date de modification
    if (projetModifie && clientInfo?.codeRef) {
      const match = clientInfo.codeRef.match(/^(.+_V)(\d+)$/);
      if (match) {
        const newV = String(parseInt(match[2]) + 1).padStart(2, "0");
        clientInfo = {
          ...clientInfo,
          codeRef: match[1] + newV,
          dateModification: now.toISOString(),
        };
        onClientInfoUpdate && onClientInfoUpdate(clientInfo);
      }
    }

    const donneesFinales = {
      ...(clientInfo !== donneesProjet?.clientInfo ? { ...donneesProjet, clientInfo } : donneesProjet),
      solution_retenue: solutionRetenue,
    };

    const nom = nomProjet.trim() ||
      (clientInfo?.codeRef ? `${clientInfo.nomEntreprise || "Projet"} — ${clientInfo.codeRef}` : `Projet ${now.toLocaleDateString("fr-FR")}`);

    const storageKey = `geo5_projets_${userId || "guest"}`;
    const projets = JSON.parse(localStorage.getItem(storageKey) || "[]");

    const id = donneesFinales._projetId;
    const idx = id
      ? projets.findIndex(
          p => p.id === String(id) || p.donneesProjet?._projetId === id
        )
      : -1;
    const entree = {
      id: id ? String(id) : Date.now().toString(),
      nom,
      date: now.toISOString(),
      donneesProjet: donneesFinales,
    };
    if (idx >= 0) {
      projets[idx] = entree;
    } else {
      projets.push(entree);
    }
    localStorage.setItem(storageKey, JSON.stringify(projets));
    setSaveMsg("✅ Projet enregistré !");
    setNomProjet("");
    setTimeout(() => { setSaveMsg(""); setShowSave(false); }, 1800);
  };

  // ── Enregistrer sous ────────────────────────────────────────────────────────
  const genererVarianteCodeRef = (codeRefSource, projetsExistants) => {
    const codesExistants = new Set(
      projetsExistants.map(p => p.donneesProjet?.clientInfo?.codeRef).filter(Boolean)
    );
    let base, numDepart;
    const match = (codeRefSource || '').match(/^(.+_V)(\d+)$/);
    if (match) {
      base = match[1];
      numDepart = parseInt(match[2]) + 1;
    } else if (codeRefSource) {
      base = codeRefSource + '_V';
      numDepart = 2;
    } else {
      base = 'COPIE_V';
      numDepart = 1;
    }
    let num = numDepart;
    let candidat = base + String(num).padStart(2, '0');
    while (codesExistants.has(candidat) && num < 99) {
      num++;
      candidat = base + String(num).padStart(2, '0');
    }
    return candidat;
  };

  const ouvrirModalSaveAs = () => {
    const sourceNom      = donneesProjet?.clientInfo?.nomEntreprise || '';
    const sourceCodeRef  = donneesProjet?.clientInfo?.codeRef       || '';
    const storageKeySA   = `geo5_projets_${userId || "guest"}`;
    const projets        = JSON.parse(localStorage.getItem(storageKeySA) || '[]');
    setSaveAsNomEntreprise(sourceNom);
    setSaveAsCodeRef(genererVarianteCodeRef(sourceCodeRef, projets));
    setShowSaveAsModal(true);
  };

  const handleEnregistrerSous = () => {
    const nom     = saveAsNomEntreprise.trim();
    const codeRef = saveAsCodeRef.trim();
    if (!nom || !codeRef) return;

    const nouveauProjetId  = Date.now();
    const nouvellesDonnees = {
      ...donneesProjet,
      clientInfo: { ...donneesProjet?.clientInfo, nomEntreprise: nom, codeRef },
      _projetId:  nouveauProjetId,
    };

    const storageKeySA  = `geo5_projets_${userId || "guest"}`;
    const projets       = JSON.parse(localStorage.getItem(storageKeySA) || '[]');
    const date          = new Date().toISOString();
    const nouvelleEntree = {
      id:             String(nouveauProjetId),
      nom:            `${nom} — ${codeRef}`,
      date,
      donneesProjet:  nouvellesDonnees,
    };
    projets.push(nouvelleEntree);
    localStorage.setItem(storageKeySA, JSON.stringify(projets));

    setShowSaveAsModal(false);
    if (onProjetClone) onProjetClone(nouvelleEntree);
  };

  const handleRetenirSolution = (solution) => {
    const recommandeeId = diagnosticBackend?.adequation?.classement?.[0]?.id || null;
    const isCurrent = solutionRetenue?.id === solution.id;
    const nouvelleRetenue = isCurrent ? null : {
      id:          solution.id,
      label:       solution.nom,
      date_iso:    new Date().toISOString(),
      recommandee: recommandeeId,
      ecart_motif: "",
    };
    setSolutionRetenue(nouvelleRetenue);
    onSolutionRetenue?.(nouvelleRetenue);
    const projetId = donneesProjet?._projetId;
    if (!projetId) return;
    const storageKey = `geo5_projets_${userId || "guest"}`;
    const projets = JSON.parse(localStorage.getItem(storageKey) || "[]");
    const idx = projets.findIndex(p => p.id === String(projetId) || p.donneesProjet?._projetId === projetId);
    if (idx >= 0) {
      projets[idx].donneesProjet = { ...projets[idx].donneesProjet, solution_retenue: nouvelleRetenue };
      localStorage.setItem(storageKey, JSON.stringify(projets));
    }
  };

  const labelOuvrage = donneesProjet?.typeOuvrage
    ? (t(`solution_page.type_ouvrage.${donneesProjet.typeOuvrage}`, { defaultValue: donneesProjet.typeOuvrage }))
    : "";


  // ═══ LEGACY FALLBACK — Phase 8D-B1 ═══
  // Alertes EN 1997-2 visibles : source backend en mode BACKEND, frontend en FALLBACK
  // En mode BACKEND : alertesEN2 reste vide (bloc appelEN1997_2 non exécuté)
  // En mode FALLBACK : alertesEN2 rempli par /analyser/en1997-2
  const alertesEN1997Visibles =
    sourceMode === "BACKEND"
      ? (diagnosticBackend?.alertes?.en1997_2 || [])
      : (alertesEN2 || []);

  // ═══ LEGACY FALLBACK — Phase 8D-B1 ═══
  // Missions géotechniques visibles : source backend en mode BACKEND (R9)
  // Aucune génération de mission côté frontend en mode BACKEND
  const missionsVisibles =
    sourceMode === "BACKEND"
      ? (diagnosticBackend?.alertes?.missions || [])
      : (missionsEN2 || []);

  return (
    <div style={{ fontFamily:"'Segoe UI', system-ui, sans-serif", background:GV.blanc, minHeight:"100vh" }}>

      {/* ══ BARRE D'ACTIONS ══ */}
      <div style={{ background:"#F8F9FA", borderBottom:`2px solid ${GV.border}`, padding:"10px 48px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>

          {/* Résumé projet */}
          <div style={{ fontSize:13, color:GV.grisMid, fontWeight:600 }}>
            {donneesProjet?.typeOuvrage
              ? <span>📐 {labelOuvrage} · H = <strong>{donneesProjet.hauteur}m</strong> · {donneesProjet.typeSol}</span>
              : <span style={{ color:"#aaa" }}>Analyse en cours...</span>
            }
          </div>

          <div style={{ display:"flex", gap:8, alignItems:"center" }}>

            {/* Modifier */}
            <div style={{ position:"relative" }}>
              <button onClick={() => { setShowModifier(v => !v); setShowSave(false); }} style={{
                background:"#fff", border:`1.5px solid ${GV.border}`, color:GV.dark,
                borderRadius:8, padding:"7px 14px", cursor:"pointer", fontSize:13, fontWeight:600
              }}>{t("solution_page.modifier")}</button>
              {showModifier && (
                <div style={{
                  position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:300,
                  background:"#fff", border:`1px solid ${GV.border}`, borderRadius:10,
                  padding:8, minWidth:240, boxShadow:"0 6px 20px rgba(0,0,0,0.12)"
                }}>
                  <div style={{ fontSize:11, color:GV.grisMid, fontWeight:700, padding:"4px 8px 8px", borderBottom:`1px solid ${GV.border}`, marginBottom:6 }}>
                    {t("solution_page.retourner_etape")}
                  </div>
                  {(t("solution_page.etapes_modifier", { returnObjects: true }) || []).map((e, i) => (
                    <button key={i} onClick={() => { onModifier && onModifier(i); setShowModifier(false); }} style={{
                      display:"block", width:"100%", textAlign:"left",
                      background:"transparent", border:"none",
                      color:GV.dark, padding:"9px 10px", fontSize:13, cursor:"pointer",
                      borderRadius:7, fontWeight:500,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background=GV.vertLight}
                    onMouseLeave={e => e.currentTarget.style.background="transparent"}
                    >{e.emoji} Étape {e.label}</button>
                  ))}
                </div>
              )}
            </div>

            {/* Sauvegarder */}
            <div style={{ position:"relative" }}>
              <button onClick={() => { setShowSave(v => !v); setShowModifier(false); }} style={{
                background:GV.vert, border:"none", color:"#fff",
                borderRadius:8, padding:"7px 14px", cursor:"pointer", fontSize:13, fontWeight:700
              }}>{t("solution_page.sauvegarder")}</button>
              {showSave && (
                <div style={{
                  position:"absolute", top:"calc(100% + 6px)", right:0, zIndex:300,
                  background:"#fff", border:`1px solid ${GV.border}`, borderRadius:10,
                  padding:14, minWidth:280, boxShadow:"0 6px 20px rgba(0,0,0,0.12)"
                }}>
                  {saveMsg ? (
                    <div style={{ color:GV.vert, fontWeight:700, fontSize:14, textAlign:"center", padding:"8px 0" }}>{saveMsg}</div>
                  ) : (
                    <>
                      <div style={{ fontSize:11, color:GV.grisMid, fontWeight:700, marginBottom:8 }}>{t("solution_page.nom_projet")}</div>
                      <input
                        value={nomProjet}
                        onChange={e => setNomProjet(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && sauvegarderProjet()}
                        placeholder={`ex: Mur de soutènement — ${new Date().toLocaleDateString("fr-FR")}`}
                        style={{
                          width:"100%", boxSizing:"border-box",
                          background:"#fff", border:`1px solid ${GV.border}`,
                          borderRadius:7, padding:"8px 10px", color:GV.dark,
                          fontSize:13, marginBottom:10, outline:"none",
                        }}
                      />
                      <button onClick={sauvegarderProjet} style={{
                        width:"100%", background:GV.vert, border:"none",
                        borderRadius:7, padding:"9px", color:"#fff",
                        fontSize:13, fontWeight:700, cursor:"pointer"
                      }}>{t("solution_page.enregistrer")}</button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Enregistrer sous */}
            <button onClick={ouvrirModalSaveAs} style={{
              background:"#fff", border:`1px solid ${GV.vert}`, color:GV.vert,
              borderRadius:8, padding:"7px 14px", cursor:"pointer", fontSize:13, fontWeight:700
            }}>
              Enregistrer sous
            </button>

            {/* Agent IA */}
            <button onClick={onAgent} style={{
              background:"#0f172a", border:"none", color:"#fff",
              borderRadius:8, padding:"7px 14px", cursor:"pointer", fontSize:13, fontWeight:700
            }}>
              {t("solution_page.agent_ia")}
            </button>

          </div>
        </div>
      </div>

      {/* ══ BANDEAU CLIENT ══ */}
      {donneesProjet?.clientInfo?.nomEntreprise && (
        <div style={{ background:"#F8F9FA", borderBottom:"1px solid #E0E0E0", padding:"10px 48px" }}>
          <div style={{ maxWidth:1100, margin:"0 auto", display:"flex", alignItems:"center", gap:24, flexWrap:"wrap" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:14 }}>🏢</span>
              <span style={{ fontSize:13, fontWeight:800, color:"#1a1a1a" }}>{donneesProjet.clientInfo.nomEntreprise}</span>
              <span style={{ fontSize:11, color:"#6B7280", background:"#E5E7EB", borderRadius:4, padding:"2px 7px" }}>{donneesProjet.clientInfo.typeClient}</span>
            </div>
            {donneesProjet.clientInfo.wilaya && (
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:13 }}>📍</span>
                <span style={{ fontSize:12, color:"#374151" }}>{donneesProjet.clientInfo.wilaya.nom}</span>
                <span style={{ fontSize:11, fontWeight:700, color:"#2D7A2D", background:"#E8F5E9", borderRadius:4, padding:"2px 7px" }}>Zone {donneesProjet.clientInfo.wilaya.zone}</span>
              </div>
            )}
            {donneesProjet.clientInfo.codeRef && (
              <div style={{ fontSize:12, fontFamily:"monospace", fontWeight:700, color:"#6A1B9A", background:"#EDE7F6", borderRadius:4, padding:"3px 10px" }}>
                {donneesProjet.clientInfo.codeRef}
              </div>
            )}
            {donneesProjet.clientInfo.budget && (
              <div style={{ fontSize:12, color:"#1B5E20", fontWeight:600 }}>
                💰 Budget : {parseFloat(donneesProjet.clientInfo.budget).toLocaleString("fr-FR")} EUR
              </div>
            )}
            <div style={{ marginLeft:"auto", display:"flex", flexDirection:"column", alignItems:"flex-end", gap:2 }}>
              {(donneesProjet.clientInfo.nomContact || donneesProjet.clientInfo.prenomContact) && (
                <div style={{ fontSize:12, color:"#6B7280" }}>
                  Contact : {donneesProjet.clientInfo.prenomContact} {donneesProjet.clientInfo.nomContact}
                  {donneesProjet.clientInfo.mobile && ` · ${donneesProjet.clientInfo.mobile}`}
                </div>
              )}
              {donneesProjet.clientInfo.dateModification && (
                <div style={{ fontSize:11, color:"#9C27B0", fontWeight:600 }}>
                  ✏️ Modifié le {new Date(donneesProjet.clientInfo.dateModification).toLocaleDateString("fr-FR", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ CONTENU ══ */}
      <div style={{ maxWidth:1100, margin:"0 auto", padding:"40px 24px" }}>

        {/* ── ANIMATION D'ANALYSE ── */}
        {analysing ? (
          <div style={{ textAlign:"center", padding:"80px 20px" }}>
            <div style={{ fontSize:56, marginBottom:24, animation:"spin 2s linear infinite", display:"inline-block" }}>⚙️</div>
            <h2 style={{ fontSize:24, fontWeight:800, color:GV.dark, margin:"0 0 12px" }}>
              {t("solution_page.analyse_titre")}
            </h2>
            <p style={{ fontSize:15, color:GV.grisMid, margin:"0 0 32px" }}>
              {t("solution_page.analyse_desc")}
            </p>
            <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
              {[t("solution_page.analyse_tag1"), t("solution_page.analyse_tag2"), t("solution_page.analyse_tag3")].map((tag,i) => (
                <div key={i} style={{
                  background:GV.vertLight, border:`1px solid ${GV.vertMid}40`,
                  borderRadius:99, padding:"6px 16px", fontSize:12, color:GV.vertDark,
                  fontWeight:600, animation:`pulse 1.5s ease-in-out ${i*0.4}s infinite`
                }}>{tag}</div>
              ))}
            </div>
            <style>{`
              @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
              @keyframes pulse { 0%,100%{opacity:0.5} 50%{opacity:1} }
            `}</style>
          </div>
        ) : (
          <>
            {/* ── FALLBACK MINIMAL (EN-PREP-1a.2) ── */}
            {/* Affiché UNIQUEMENT après tentative /diagnostic (pas de flash au 1er render) */}
            {/* analysing=true → spinner ; analysing=false + !diagnosticAttempted → placeholder neutre */}
            {showFallbackMinimal && (
              diagnosticBackend?.eligibilite?.eligibles?.length > 0 ? (
                /* Cas 1 : backend a répondu avec éligibles, mais sans classement */
                <div style={{
                  background:"#FFF7ED", border:"1.5px solid #F59E0B", borderRadius:10,
                  padding:"16px 20px", marginBottom:20,
                  fontSize:13, color:"#92400E",
                }}>
                  <div style={{ fontWeight:700, marginBottom:8 }}>
                    ⚠️ Moteur de classement indisponible — solutions éligibles (non classées) :
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                    {diagnosticBackend.eligibilite.eligibles.map(id => (
                      <span key={id} style={{
                        background:"#FEF3C7", border:"1px solid #F59E0B",
                        borderRadius:6, padding:"3px 10px",
                        fontSize:12, fontWeight:600, color:"#78350F",
                      }}>
                        {formatSolutionId(id)}
                      </span>
                    ))}
                  </div>
                  <div style={{ marginTop:10, fontSize:11, color:"#B45309", fontStyle:"italic" }}>
                    Aucun classement disponible — relancez l'analyse ou vérifiez le serveur.
                  </div>
                </div>
              ) : (
                /* Cas 2 : backend totalement KO (diagnosticBackend null ou eligibles vide) */
                <div style={{
                  background:"#FFF7ED", border:"1.5px solid #F59E0B", borderRadius:10,
                  padding:"16px 20px", marginBottom:20,
                  display:"flex", alignItems:"center", justifyContent:"space-between",
                  gap:12, fontSize:13, color:"#92400E", fontWeight:600,
                }}>
                  <span>⚠️ Service d'analyse indisponible — vérifiez la connexion au serveur.</span>
                  <button
                    onClick={() => window.location.reload()}
                    style={{
                      background:"#F59E0B", color:"#fff",
                      border:"none", borderRadius:6, padding:"6px 14px",
                      fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap",
                    }}
                  >
                    Réessayer
                  </button>
                </div>
              )
            )}

            {/* ── DIAGNOSTIC RENDER ── */}
            {/* ── RÉSUMÉ DU PROJET ── */}
            <div style={{
              background:GV.vertLight, border:`1.5px solid ${GV.vertMid}40`,
              borderRadius:14, padding:"20px 28px", marginBottom:32,
              display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:16
            }}>
              <div>
                <div style={{ fontSize:12, color:GV.vertClair, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>{t("solution_page.projet_analyse")}</div>
                <div style={{ fontSize:18, fontWeight:800, color:GV.vertDark }}>{labelOuvrage}</div>
                <div style={{ fontSize:13, color:GV.grisMid, marginTop:4 }}>
                  {donneesProjet?.hauteur && `Hauteur : ${donneesProjet.hauteur}m`}
                  {donneesProjet?.longueur && ` · Longueur : ${donneesProjet.longueur}m`}
                  {donneesProjet?.typeSol && ` · Sol : ${donneesProjet.typeSol}`}
                  {donneesProjet?.zoneSismique && ` · Zone sismique : ${donneesProjet.zoneSismique}`}
                </div>
              </div>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:32, fontWeight:900, color:GV.vert }}>{diagnosticBackend?.adequation?.classement?.length || 0}</div>
                <div style={{ fontSize:12, color:GV.grisMid, textTransform:"uppercase", letterSpacing:1 }}>{t("solution_page.solutions_trouvees")}</div>
              </div>
            </div>

            {/* ── BADGE TRANSPARENCE — fondation_batiment uniquement ── */}
            {/* sol_fondation absent du payload /diagnostic : utilise sol_critique (couche dominante). */}
            {/* Limitation bicouche : le sol affiché est le sol critique de surface, pas forcément le sol d'assise à D. */}
            {donneesProjet?.typeOuvrage === "fondation_batiment" && diagnosticBackend && (
              <div style={{
                display:"inline-flex", alignItems:"center", gap:6,
                background:"#EFF6FF", border:"1px solid #BFDBFE",
                borderRadius:8, padding:"7px 14px", marginBottom:20,
                fontSize:12, color:"#1E40AF",
              }}>
                📍 Fondation jugée sur :&nbsp;
                <strong>{diagnosticBackend.faits?.sol_critique || donneesProjet.typeSol || "—"}</strong>
                {donneesProjet.profondeur_encastrement
                  ? <span>&nbsp;à D&nbsp;=&nbsp;<strong>{donneesProjet.profondeur_encastrement}&nbsp;m</strong></span>
                  : null
                }
              </div>
            )}

            {/* ── SOLUTION(S) RECOMMANDÉE(S) ── */}
            {(() => {
              // Cas H > 6m : afficher Solution I et Solution II côte à côte
              const solI  = solutions.find(s => s.id === "deux_murs_cantilever");
              const solII = solutions.find(s => s.id === "deux_murs_gabions");
              const H = parseFloat(donneesProjet?.hauteur) || 0;

              if (solI && solII) {
                const phi = parseFloat(donneesProjet?.frottement) || 20;
                const L = solI.L_influence;
                return (
                  <div style={{ marginBottom:28 }}>
                    {/* Bandeau référence */}
                    <div style={{ background:"#FFF7ED", border:"1.5px solid #F59E0B", borderRadius:10, padding:"12px 18px", marginBottom:16 }}>
                      <div style={{ fontSize:13, fontWeight:800, color:"#92400E", marginBottom:4 }}>
                        ⚠️ H = {H}m &gt; 6m — Mur unique déconseillé
                      </div>
                      <div style={{ fontSize:12, color:"#78716c", lineHeight:1.7 }}>
                        Au-delà de 6m, le mur cantilever unique devient techniquement défavorable
                        (B ≈ {(H*0.8).toFixed(1)}m, ferraillage élevé, glissement sismique critique).<br/>
                        <strong>Réf. :</strong> DTU 13.12 §3 · SETRA "Les ouvrages de soutènement" (1998) ·
                        Techniques de l'Ingénieur C244 · GEO5 Manuel N°2 (H optimal : 2–6m)
                      </div>
                    </div>

                    {/* Label */}
                    <div style={{ fontSize:12, color:GV.vertClair, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:14 }}>
                      {t("solution_page.murs_stages")} — 2 × {(H/2).toFixed(1)}m
                    </div>

                    {/* Deux solutions côte à côte */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                      {[solI, solII].map((sol) => (
                        <div key={sol.id} style={{
                          border:`2px solid ${sol.est_gabion_gradins ? "#16a34a" : GV.vert}`,
                          borderRadius:14,
                          background: sol.est_gabion_gradins
                            ? "linear-gradient(135deg,#f0fdf4,#fff)"
                            : `linear-gradient(135deg,${GV.vertLight},#fff)`,
                          padding:"20px 22px",
                          boxShadow:`0 4px 16px ${sol.est_gabion_gradins ? "#16a34a" : GV.vert}20`
                        }}>
                          {/* Header solution */}
                          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:14 }}>
                            <div>
                              <div style={{ fontSize:11, fontWeight:800, color: sol.est_gabion_gradins ? "#15803d" : GV.vertDark, letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>
                                {sol.nom.split("—")[0].trim()}
                              </div>
                              <div style={{ fontSize:18, fontWeight:900, color:GV.dark }}>
                                {sol.icone} {sol.nom.split("—")[1]?.trim()}
                              </div>
                            </div>
                            <div style={{ textAlign:"center", background: sol.est_gabion_gradins ? "#16a34a" : GV.vert, borderRadius:10, padding:"8px 14px", flexShrink:0 }}>
                              <div style={{ fontSize:13, fontWeight:800, color:"#fff", padding:"4px 0" }}>{getLabelNiveau(sol)}</div>
                              <div style={{ fontSize:9, color:"rgba(255,255,255,0.8)", textTransform:"uppercase" }}>Adéquation</div>
                            </div>
                          </div>

                          {/* Modules GEO5 */}
                          <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginBottom:14 }}>
                            {getModulesGEO5(sol.id, diagnosticBackend, sol).map(m => (
                              <span key={m} style={{
                                background: sol.est_gabion_gradins ? "#16a34a" : GV.vert,
                                color:"#fff", borderRadius:99, padding:"2px 10px", fontSize:10, fontWeight:700
                              }}>{m}</span>
                            ))}
                          </div>

                          {/* Avantages */}
                          <div style={{ marginBottom:10 }}>
                            <div style={{ fontSize:11, fontWeight:700, color: sol.est_gabion_gradins ? "#15803d" : GV.vert, marginBottom:6, textTransform:"uppercase", letterSpacing:0.8 }}>✅ {t("solution_page.avantages")}</div>
                            {sol.avantages.map((a,i) => (
                              <div key={i} style={{ fontSize:12, color:GV.dark, padding:"3px 0", borderBottom:`1px solid ${GV.border}` }}>▸ {a}</div>
                            ))}
                          </div>

                          {/* Points d'attention */}
                          {sol.inconvenients?.length > 0 && (
                            <div>
                              <div style={{ fontSize:11, fontWeight:700, color:"#E65100", marginBottom:6, textTransform:"uppercase", letterSpacing:0.8 }}>⚠ {t("solution_page.points_attention")}</div>
                              {sol.inconvenients.map((a,i) => (
                                <div key={i} style={{ fontSize:12, color:GV.dark, padding:"3px 0", borderBottom:`1px solid ${GV.border}` }}>▸ {a}</div>
                              ))}
                            </div>
                          )}
                          {/* Bouton Retenir */}
                          {(() => {
                            const estRetenuDM = solutionRetenue?.id === sol.id;
                            return (
                              <div style={{ marginTop:10, textAlign:"right" }}>
                                <button
                                  onClick={() => handleRetenirSolution(sol)}
                                  style={{
                                    background: estRetenuDM ? "#059669" : "transparent",
                                    border: `1.5px solid ${estRetenuDM ? "#059669" : "#CBD5E1"}`,
                                    color: estRetenuDM ? "#fff" : "#475569",
                                    borderRadius: 6, padding: "4px 12px",
                                    fontSize: 11, fontWeight: 700, cursor: "pointer",
                                  }}
                                >
                                  {estRetenuDM ? "✓ Retenu — annuler" : "Retenir cette solution"}
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      ))}
                    </div>

                    {/* Zone d'interférence commune */}
                    <div style={{ marginTop:14, background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:10, padding:"12px 16px" }}>
                      <div style={{ fontSize:12, fontWeight:800, color:"#1E40AF", marginBottom:6 }}>
                        📐 Vérification d'interférence — commune aux 2 solutions
                      </div>
                      <div style={{ fontSize:12, color:"#1E40AF", lineHeight:1.7 }}>
                        Zone d'influence : <strong>L = H/2 / tan(45+φ/2) = {(H/2).toFixed(1)} / tan(45+{phi}/2) = {L}m</strong><br/>
                        Si D &gt; {L}m entre les 2 murs → <strong style={{ color:"#16a34a" }}>pas d'interférence ✅</strong><br/>
                        Si D ≤ {L}m → <strong style={{ color:"#E65100" }}>ajouter le poids du mur supérieur comme surcharge sur le mur inférieur dans GEO5</strong>
                      </div>
                    </div>

                    {/* Alerte modules manquants pour solI */}
                    <AlerteModulesManquants solutionModules={getModulesGEO5(solI.id, diagnosticBackend, solI)} profilUtilisateur={profilUtilisateur} />
                  </div>
                );
              }

              // Cas normal : afficher best seul
              if (!displayBest) return null;
              const bestEstRetenu = solutionRetenue?.id === displayBest.id;
              return (
                <div style={{ marginBottom:28 }}>
                  {sourceMode === "BACKEND" && (
                    <div style={{
                      display:"inline-flex", alignItems:"center", gap:6,
                      background:"#F0FDF4", border:"1px solid #86EFAC",
                      borderRadius:8, padding:"6px 14px", marginBottom:12,
                      fontSize:11, fontWeight:700, color:"#166534",
                    }}>
                      🧠 Classement calculé par le moteur backend
                    </div>
                  )}
                  <div style={{ fontSize:12, color:GV.vertClair, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:14 }}>
                    {t("solution_page.recommandee")}
                  </div>
                  <div style={{
                    border: bestEstRetenu ? "3px solid #059669" : `2px solid ${GV.vert}`,
                    borderRadius:16,
                    background:`linear-gradient(135deg, ${GV.vertLight}, #ffffff)`,
                    padding:"24px 28px",
                    boxShadow: bestEstRetenu ? `0 0 0 2px #059669, 0 4px 20px ${GV.vert}20` : `0 4px 20px ${GV.vert}20`,
                  }}>
                    {bestEstRetenu && (
                      <div style={{
                        display:"inline-flex", alignItems:"center", gap:4,
                        background:"#F0FDF4", border:"1px solid #86EFAC",
                        borderRadius:6, padding:"3px 10px", marginBottom:12,
                        fontSize:11, fontWeight:700, color:"#166534",
                      }}>
                        ✓ Solution retenue
                      </div>
                    )}
                    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:16 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                        <div style={{ fontSize:44 }}>{displayBest.icone}</div>
                        <div>
                          <div style={{ fontSize:22, fontWeight:900, color:GV.vertDark, marginBottom:4 }}>{displayBest.nom}</div>
                          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                            {getModulesGEO5(displayBest.id, diagnosticBackend, displayBest).map(m => (
                              <span key={m} style={{ background:GV.vert, color:GV.blanc, borderRadius:99, padding:"3px 12px", fontSize:11, fontWeight:700 }}>{m}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign:"center", background:GV.vert, borderRadius:12, padding:"12px 20px" }}>
                        <div style={{ fontSize:13, fontWeight:800, color:GV.blanc, padding:"4px 0" }}>{getLabelNiveau(displayBest)}</div>
                        <div style={{ fontSize:11, color:"rgba(255,255,255,0.8)", textTransform:"uppercase", letterSpacing:1 }}>{t("solution_page.adequation")}</div>
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginTop:20 }}>
                      <div>
                        <div style={{ fontSize:12, fontWeight:700, color:GV.vert, marginBottom:8, textTransform:"uppercase", letterSpacing:1 }}>✅ {t("solution_page.avantages")}</div>
                        {displayBest.avantages.map((a,i) => (
                          <div key={i} style={{ fontSize:13, color:GV.dark, padding:"4px 0", borderBottom:`1px solid ${GV.border}` }}>▸ {a}</div>
                        ))}
                      </div>
                      {displayBest.inconvenients?.length > 0 && (
                        <div>
                          <div style={{ fontSize:12, fontWeight:700, color:"#E65100", marginBottom:8, textTransform:"uppercase", letterSpacing:1 }}>⚠ {t("solution_page.points_attention")}</div>
                          {displayBest.inconvenients.map((a,i) => (
                            <div key={i} style={{ fontSize:13, color:GV.dark, padding:"4px 0", borderBottom:`1px solid ${GV.border}` }}>▸ {a}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop:16, textAlign:"right" }}>
                      <button
                        onClick={() => handleRetenirSolution(displayBest)}
                        style={{
                          background: bestEstRetenu ? "#059669" : "transparent",
                          border: `1.5px solid ${bestEstRetenu ? "#059669" : "#CBD5E1"}`,
                          color: bestEstRetenu ? "#fff" : "#475569",
                          borderRadius: 7, padding: "6px 16px",
                          fontSize: 12, fontWeight: 700, cursor: "pointer",
                        }}
                      >
                        {bestEstRetenu ? "✓ Retenu — annuler" : "Retenir cette solution"}
                      </button>
                    </div>
                  </div>
                  <AlerteModulesManquants solutionModules={getModulesGEO5(displayBest.id, diagnosticBackend, displayBest)} profilUtilisateur={profilUtilisateur} />
                </div>
              );
            })()}

            {/* ══ BLOC 2 MURS EN GRADINS — affiché si solution recommandée = deux_murs ══ */}
            {displayBest?.deux_murs && (
              <div style={{ marginBottom:28, border:"2px solid #F59E0B", borderRadius:16, overflow:"hidden" }}>
                {/* Bandeau explication */}
                <div style={{ background:"linear-gradient(135deg,#92400E,#b45309)", padding:"18px 24px" }}>
                  <div style={{ fontSize:16, fontWeight:800, color:"#fff", marginBottom:6 }}>
                    📐 Pourquoi des murs étagés ?
                  </div>
                  <div style={{ fontSize:13, color:"rgba(255,255,255,0.9)", lineHeight:1.7 }}>
                    Pour H = {donneesProjet?.hauteur}m &gt; 6m, un mur unique devient difficile à stabiliser sous séisme
                    (glissement sismique critique, semelle très large, coût élevé). La solution recommandée est
                    de réaliser <strong>2 murs de {displayBest.H_sup}m chacun</strong>, l'un en aval et l'autre en amont du talus,
                    chacun dimensionné indépendamment dans GEO5 Murs Cantilever.
                  </div>
                </div>

                {/* Points clés */}
                <div style={{ background:"#FFF7ED", padding:"16px 24px", borderBottom:"1px solid #FED7AA" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16 }}>
                    {[
                      { icon:"🏗️", titre:"Mur amont (supérieur)", val:`H = ${displayBest.H_sup}m`, note:"Dimensionner en premier" },
                      { icon:"🧱", titre:"Mur aval (inférieur)", val:`H = ${displayBest.H_inf}m`, note:"Ajouter surcharge si interférence" },
                      { icon:"📏", titre:"Zone d'influence", val:`L = ${displayBest.L_influence}m`, note:`φ=${displayBest.phi}° → L=H/tan(45+φ/2)` },
                    ].map(c => (
                      <div key={c.titre} style={{ background:"#fff", borderRadius:10, padding:"12px 14px", border:"1px solid #FED7AA" }}>
                        <div style={{ fontSize:22, marginBottom:4 }}>{c.icon}</div>
                        <div style={{ fontSize:12, color:"#92400E", fontWeight:700, marginBottom:2 }}>{c.titre}</div>
                        <div style={{ fontSize:18, fontWeight:900, color:"#b45309" }}>{c.val}</div>
                        <div style={{ fontSize:11, color:"#78716c", marginTop:2 }}>{c.note}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Calculateur interférence intégré */}
                <div style={{ background:"#fff", padding:"20px 24px" }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
                    <div style={{ fontSize:14, fontWeight:800, color:"#92400E" }}>
                      🔢 Vérification d'interférence — Simulateur intégré
                    </div>
                    <button
                      onClick={() => setShowCalc(!showCalc)}
                      style={{ background:"#b45309", border:"none", borderRadius:8, padding:"8px 18px",
                        color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer" }}>
                      {showCalc ? "▲ Masquer" : "▼ Ouvrir le simulateur"}
                    </button>
                  </div>
                  {!showCalc && (
                    <div style={{ fontSize:13, color:"#78716c", lineHeight:1.7,
                      background:"#FEF3C7", borderRadius:8, padding:"12px 16px" }}>
                      ℹ️ <strong>Règle :</strong> Si la distance D entre les deux murs est &gt; {displayBest.L_influence}m → pas d'interférence.<br/>
                      Si D ≤ {displayBest.L_influence}m → le poids du mur supérieur doit être ajouté comme surcharge sur le mur inférieur dans GEO5.
                    </div>
                  )}
                  {showCalc && <CalculateurDeuxMurs H_init={parseFloat(donneesProjet?.hauteur) || 6.9} phi_init={displayBest.phi} />}
                </div>
              </div>
            )}

            {/* ══ BLOC GABIONS — affiché si solution recommandée = gabions ══ */}
            {displayBest?.est_gabion && (
              <div style={{ marginBottom:28, border:"2px solid #16a34a", borderRadius:16, overflow:"hidden" }}>
                <div style={{ background:"linear-gradient(135deg,#14532d,#16a34a)", padding:"18px 24px" }}>
                  <div style={{ fontSize:16, fontWeight:800, color:"#fff", marginBottom:6 }}>
                    🪨 Pourquoi les Gabions ?
                  </div>
                  <div style={{ fontSize:13, color:"rgba(255,255,255,0.9)", lineHeight:1.7 }}>
                    Pour H = {donneesProjet?.hauteur}m avec espace disponible, le mur gabions est souvent
                    <strong> plus économique, plus esthétique</strong> et ne nécessite <strong>aucun ferraillage</strong>.
                    Cas réel France (H=6m, Zone 3) : SF glissement permanent = 2.09, SF glissement sismique = 1.26.
                  </div>
                </div>
                <div style={{ background:"#f0fdf4", padding:"16px 24px", borderBottom:"1px solid #bbf7d0" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12 }}>
                    {[
                      { icon:"📦", titre:"Blocs standard", val:"2m × 1m", note:"Largeur × Hauteur" },
                      { icon:"📐", titre:"Décalage (fruit)", val:"0.50m/bloc", note:"≈ 8% H_bloc" },
                      { icon:"🪨", titre:"Remplissage φ", val:"35°", note:"Pierres concassées" },
                      { icon:"⚖️", titre:"SF glissement", val:"2.09 perm.", note:"1.26 sismique Zone IV" },
                    ].map(c => (
                      <div key={c.titre} style={{ background:"#fff", borderRadius:10, padding:"10px 12px", border:"1px solid #bbf7d0", textAlign:"center" }}>
                        <div style={{ fontSize:20, marginBottom:3 }}>{c.icon}</div>
                        <div style={{ fontSize:11, color:"#15803d", fontWeight:700, marginBottom:2 }}>{c.titre}</div>
                        <div style={{ fontSize:16, fontWeight:900, color:"#14532d" }}>{c.val}</div>
                        <div style={{ fontSize:10, color:"#6b7280", marginTop:2 }}>{c.note}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ background:"#fff", padding:"14px 24px" }}>
                  <div style={{ fontSize:12, color:"#15803d", fontWeight:700, marginBottom:8 }}>
                    📋 Paramètres GEO5 Gabions — valeurs du cas réel
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, fontSize:12 }}>
                    {[
                      ["Méthode poussée", "Coulomb (CSN 730037)"],
                      ["Méthode butée passive", "Caquot-Kérisel"],
                      ["SFo perm. / sism.", "1.50 / 1.30"],
                      ["SFs perm. / sism.", "1.50 / 1.25"],
                      ["SFb portance", "1.00 (perm. et sism.)"],
                      ["γ remplissage", "19 kN/m³ | φ=35° | c=0"],
                      ["Treillis Rt / Rs", "53.96 / 32.38 kN/m"],
                      ["Séisme Kh Zone IV", "0.125 | Kv=0.0375"],
                    ].map(([k,v]) => (
                      <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"5px 8px", background:"#f0fdf4", borderRadius:6 }}>
                        <span style={{ color:"#6b7280" }}>{k}</span>
                        <span style={{ fontWeight:700, color:"#14532d" }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── AUTRES SOLUTIONS — hiérarchie par niveau ── */}
            {displayOthers.length > 0 && (() => {
              const getNiveau = (s) =>
                s.niveau ||
                s.niveau_adequation ||
                diagnosticBackend?.adequation?.solutions?.[s.id]?.niveau ||
                "COMPLÉMENTAIRE";

              const grpTop    = displayOthers.filter(s => getNiveau(s) === "TRÈS_ADAPTÉE");
              const grpAdapt  = displayOthers.filter(s => getNiveau(s) === "ADAPTÉE");
              const grpCollap = displayOthers.filter(s =>
                !["TRÈS_ADAPTÉE", "ADAPTÉE"].includes(getNiveau(s))
              );

              const renderCard = (s) => {
                const niv = getNiveau(s);
                const cfg = NIVEAU_CONFIG[niv] || NIVEAU_CONFIG["COMPLÉMENTAIRE"];
                const estRetenu = solutionRetenue?.id === s.id;
                return (
                  <div key={s.id} style={{
                    border: `1.5px solid ${cfg.color}55`,
                    borderRadius: 12,
                    background: cfg.bg,
                    padding: "18px 20px",
                    boxShadow: estRetenu ? "0 0 0 2px #059669, 0 2px 8px rgba(0,0,0,0.06)" : "0 2px 8px rgba(0,0,0,0.06)",
                  }}>
                    {estRetenu && (
                      <div style={{
                        display:"inline-flex", alignItems:"center", gap:4,
                        background:"#F0FDF4", border:"1px solid #86EFAC",
                        borderRadius:5, padding:"2px 8px", marginBottom:8,
                        fontSize:10, fontWeight:700, color:"#166534",
                      }}>
                        ✓ Solution retenue
                      </div>
                    )}
                    <div style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:10 }}>
                      <span style={{ fontSize:26, flexShrink:0 }}>{s.icone || "🧠"}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:14, fontWeight:800, color:GV.dark, marginBottom:4 }}>{s.nom}</div>
                        <span style={{
                          fontSize:11, fontWeight:700,
                          color: cfg.color,
                          background: "#fff",
                          border: `1px solid ${cfg.color}`,
                          borderRadius: 5,
                          padding: "2px 8px",
                        }}>{cfg.label}</span>
                      </div>
                    </div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:6 }}>
                      {(displayModules[s.id] || getModulesGEO5(s.id, diagnosticBackend, s)).map(m => (
                        <span key={m} style={{
                          background: GV.gris, color: GV.dark, borderRadius:99,
                          padding:"2px 10px", fontSize:10, fontWeight:600,
                        }}>{m}</span>
                      ))}
                    </div>
                    <div style={{ marginTop:10, textAlign:"right" }}>
                      <button
                        onClick={() => handleRetenirSolution(s)}
                        style={{
                          background: estRetenu ? "#059669" : "transparent",
                          border: `1.5px solid ${estRetenu ? "#059669" : "#CBD5E1"}`,
                          color: estRetenu ? "#fff" : "#475569",
                          borderRadius: 6, padding: "4px 12px",
                          fontSize: 11, fontWeight: 700, cursor: "pointer",
                        }}
                      >
                        {estRetenu ? "✓ Retenu — annuler" : "Retenir cette solution"}
                      </button>
                    </div>
                  </div>
                );
              };

              return (
                <div style={{ marginBottom:36 }}>
                  <div style={{ fontSize:12, color:GV.grisMid, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:16 }}>
                    {t("solution_page.autres_solutions")}
                  </div>

                  {/* Bloc 1 — Solutions recommandées (TRÈS_ADAPTÉE) */}
                  {grpTop.length > 0 && (
                    <div style={{ marginBottom:20 }}>
                      <div style={{
                        display:"inline-flex", alignItems:"center", gap:6,
                        background:"#F0FDF4", border:"1px solid #86EFAC",
                        borderRadius:99, padding:"3px 12px", marginBottom:12,
                        fontSize:11, fontWeight:700, color:"#166534",
                      }}>
                        Solutions recommandées
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:`repeat(${Math.min(grpTop.length,3)},1fr)`, gap:14 }}>
                        {grpTop.map(renderCard)}
                      </div>
                    </div>
                  )}

                  {/* Bloc 2 — Alternatives viables (ADAPTÉE) */}
                  {grpAdapt.length > 0 && (
                    <div style={{ marginBottom: grpCollap.length > 0 ? 16 : 0 }}>
                      <div style={{
                        display:"inline-flex", alignItems:"center", gap:6,
                        background:"#F0F9FF", border:"1px solid #BAE6FD",
                        borderRadius:99, padding:"3px 12px", marginBottom:12,
                        fontSize:11, fontWeight:700, color:"#0369A1",
                      }}>
                        Alternatives viables
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:`repeat(${Math.min(grpAdapt.length,3)},1fr)`, gap:14 }}>
                        {grpAdapt.map(renderCard)}
                      </div>
                    </div>
                  )}

                  {/* Bloc 3 — Replié par défaut : AAR + PEU_ADAPTÉE + COMPLÉMENTAIRE */}
                  {grpCollap.length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowAutres(v => !v)}
                        style={{
                          display:"flex", alignItems:"center", gap:8,
                          width:"100%", background:GV.gris,
                          border:`1px solid ${GV.border}`, borderRadius:8,
                          padding:"10px 16px", cursor:"pointer",
                          fontSize:13, fontWeight:600, color:GV.grisMid,
                          textAlign:"left",
                        }}
                      >
                        <span style={{ fontSize:12 }}>{showAutres ? "▲" : "▼"}</span>
                        <span>
                          {showAutres
                            ? "Masquer les autres solutions étudiées"
                            : `Voir les ${grpCollap.length} autre${grpCollap.length > 1 ? "s" : ""} solution${grpCollap.length > 1 ? "s" : ""} étudiée${grpCollap.length > 1 ? "s" : ""} (avec réserves ou peu adaptées)`
                          }
                        </span>
                      </button>
                      {showAutres && (
                        <div style={{
                          display:"grid",
                          gridTemplateColumns:`repeat(${Math.min(grpCollap.length,3)},1fr)`,
                          gap:14, marginTop:12,
                        }}>
                          {grpCollap.map(renderCard)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── EXCLUSIONS BACKEND ── */}
            {displayExcluded.length > 0 && (() => {
              const MAX_EXCLUSIONS = 5;
              const excluesAffichees = displayExcluded.slice(0, MAX_EXCLUSIONS);
              const excluesRestantes = displayExcluded.length - MAX_EXCLUSIONS;
              return (
                <div style={{
                  margin: "16px 0",
                  padding: "16px",
                  background: "#FFF9F0",
                  border: "1px solid #F59E0B",
                  borderRadius: 8,
                  borderLeft: "4px solid #F59E0B",
                }}>
                  <div style={{
                    fontSize: 12, fontWeight: 700, color: "#92400E",
                    marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em",
                  }}>
                    ⚙️ Solutions écartées par règles expert
                  </div>

                  {excluesAffichees.map((exclu) => (
                    <div key={exclu.id} style={{
                      marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid #FDE68A",
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#78350F" }}>
                        {exclu.nom || formatSolutionId(exclu.id)}
                      </div>
                      {(exclu.regles_resolues || []).map((regle, i) => (
                        <div key={i} style={{
                          fontSize: 11, color: "#92400E",
                          marginTop: 4, paddingLeft: 12, borderLeft: "2px solid #F59E0B",
                        }}>
                          <span style={{
                            fontWeight: 600, fontFamily: "monospace",
                            background: "#FEF3C7", padding: "1px 4px",
                            borderRadius: 3, marginRight: 6,
                          }}>
                            [{regle.id}]
                          </span>
                          {regle.raison}
                        </div>
                      ))}
                    </div>
                  ))}

                  {excluesRestantes > 0 && (
                    <div style={{ fontSize: 11, color: "#B45309", fontStyle: "italic", marginTop: 4 }}>
                      + {excluesRestantes} autre{excluesRestantes > 1 ? "s solutions écartées" : " solution écartée"}
                    </div>
                  )}

                  <div style={{ fontSize: 10, color: "#B45309", marginTop: 8, fontStyle: "italic" }}>
                    Règles KB — {diagnosticBackend?.country} — {diagnosticBackend?.language}
                  </div>
                </div>
              );
            })()}

            {/* ── ADÉQUATION QUALITATIVE BACKEND ── */}
            {diagnosticBackend?.adequation?.solutions &&
             Object.keys(diagnosticBackend.adequation.solutions).length > 0 && (() => {
              const solutionsMap = diagnosticBackend.adequation.solutions;
              const classementIds = (diagnosticBackend.adequation.classement || []).map(s => s.id);
              const entries = classementIds
                .filter(id => solutionsMap[id] !== undefined)
                .map(id => [id, solutionsMap[id]]);
              const MAX = 6;
              const affichees = entries.slice(0, MAX);
              const restantes = entries.length - MAX;
              return (
                <div style={{
                  margin: "16px 0",
                  border: "1px solid #CBD5E1",
                  borderRadius: 10,
                  overflow: "hidden",
                }}>
                  <div style={{
                    padding: "10px 16px",
                    background: "#F1F5F9",
                    borderBottom: "1px solid #CBD5E1",
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#475569",
                    textTransform: "uppercase",
                  }}>
                    🧠 Adéquation qualitative backend
                  </div>
                  <div style={{ padding: "12px 16px" }}>
                    {affichees.map(([solId, data]) => {
                      const config = NIVEAU_CONFIG[data.niveau] || NIVEAU_CONFIG["COMPLÉMENTAIRE"];
                      return (
                        <div key={solId} style={{
                          marginBottom: 12,
                          padding: "10px 12px",
                          background: config.bg,
                          borderRadius: 8,
                          border: `1px solid ${config.color}22`,
                        }}>
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 6,
                          }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>
                              {data.nom || formatSolutionId(solId)}
                            </span>
                            <span style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: config.color,
                              background: "#fff",
                              border: `1px solid ${config.color}`,
                              borderRadius: 4,
                              padding: "2px 8px",
                            }}>
                              {config.label}
                            </span>
                          </div>
                          {(data.motifs_positifs || []).length > 0 && (
                            <div style={{ marginTop: 4 }}>
                              {data.motifs_positifs.map((m, i) => (
                                <div key={i} style={{ fontSize: 11, color: "#15803D", padding: "1px 0" }}>
                                  ✓ {m}
                                </div>
                              ))}
                            </div>
                          )}
                          {(data.avertissements || []).length > 0 && (
                            <div style={{ marginTop: 4 }}>
                              {data.avertissements.map((m, i) => (
                                <div key={i} style={{ fontSize: 11, color: "#D97706", padding: "1px 0" }}>
                                  ⚠ {m}
                                </div>
                              ))}
                            </div>
                          )}
                          {(data.motifs_negatifs || []).length > 0 && (
                            <div style={{ marginTop: 4 }}>
                              {data.motifs_negatifs.map((m, i) => (
                                <div key={i} style={{ fontSize: 11, color: "#DC2626", padding: "1px 0" }}>
                                  ✗ {m}
                                </div>
                              ))}
                            </div>
                          )}
                          {GEO5_COVERAGE[solId] === "A_REVOIR" && (
                            <div style={{ marginTop: 6, fontSize: 10, color: "#92400E", fontStyle: "italic" }}>
                              ⚠️ Module GEO5 à confirmer
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {restantes > 0 && (
                      <div style={{
                        fontSize: 11,
                        color: "#64748B",
                        fontStyle: "italic",
                        textAlign: "center",
                        padding: "8px 0",
                      }}>
                        + {restantes} autre{restantes > 1 ? "s" : ""} solution{restantes > 1 ? "s" : ""} évaluée{restantes > 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* ── DIAGNOSTIC NORMATIF BACKEND ── */}
            {diagnosticBackend && (
              <div style={{
                margin: "16px 0",
                border: "1px solid #CBD5E1",
                borderRadius: 10,
                overflow: "hidden",
              }}>
                <div style={{
                  padding: "10px 16px",
                  background: "#F1F5F9",
                  borderBottom: "1px solid #CBD5E1",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#475569",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}>
                  📊 Diagnostic normatif backend
                </div>

                <div style={{ padding: "12px 16px" }}>

                  {/* BLOC 1 — Normes applicables */}
                  {diagnosticBackend?.normes?.applicables?.length > 0 && (() => {
                    const { affichees, restantes } = limiterListe(diagnosticBackend.normes.applicables);
                    return (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#0369A1", marginBottom: 6 }}>
                          📋 Normes applicables
                        </div>
                        {affichees.map((norme, i) => (
                          <div key={i} style={{ fontSize: 11, color: "#0369A1", padding: "2px 0" }}>
                            <strong>{norme.code}</strong>{" — "}{norme.titre}
                          </div>
                        ))}
                        {restantes > 0 && (
                          <div style={{ fontSize: 10, color: "#64748B", fontStyle: "italic", marginTop: 4 }}>
                            + {restantes} autre{restantes > 1 ? "s" : ""}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* BLOC 2 — Alertes EN 1997-2 */}
                  {/* ═══ LEGACY FALLBACK — Phase 8D-B1 ═══ */}
                  {/* Source : alertesEN1997Visibles */}
                  {/* BACKEND  → diagnosticBackend.alertes.en1997_2 */}
                  {/* FALLBACK → alertesEN2 frontend (/analyser/en1997-2) */}
                  {alertesEN1997Visibles.length > 0 && (() => {
                    const { affichees, restantes } = limiterListe(alertesEN1997Visibles);
                    return (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#9A3412", marginBottom: 6 }}>
                          ⚠️ Alertes EN 1997-2
                        </div>
                        {affichees.map((alerte, i) => (
                          <div key={i} style={{
                            fontSize: 11, color: "#9A3412",
                            padding: "3px 0", borderBottom: "1px solid #FED7AA",
                          }}>
                            {resolveTexte(alerte)}
                          </div>
                        ))}
                        {restantes > 0 && (
                          <div style={{ fontSize: 10, color: "#64748B", fontStyle: "italic", marginTop: 4 }}>
                            + {restantes} autre{restantes > 1 ? "s" : ""}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* BLOC 3 — Missions recommandées */}
                  {diagnosticBackend?.alertes?.missions?.length > 0 && (() => {
                    const { affichees, restantes } = limiterListe(diagnosticBackend.alertes.missions);
                    return (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#15803D", marginBottom: 6 }}>
                          📌 Missions recommandées
                        </div>
                        {affichees.map((mission, i) => (
                          <div key={i} style={{ fontSize: 11, color: "#15803D", padding: "2px 0" }}>
                            → {resolveTexte(mission)}
                          </div>
                        ))}
                        {restantes > 0 && (
                          <div style={{ fontSize: 10, color: "#64748B", fontStyle: "italic", marginTop: 4 }}>
                            + {restantes} autre{restantes > 1 ? "s" : ""}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* BLOC 4 — Alertes ISO 17892 */}
                  {diagnosticBackend?.essais?.iso17892_alertes?.length > 0 && (() => {
                    const { affichees, restantes } = limiterListe(diagnosticBackend.essais.iso17892_alertes);
                    return (
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#92400E", marginBottom: 6 }}>
                          🧪 Alertes essais ISO 17892
                        </div>
                        {affichees.map((alerte, i) => (
                          <div key={i} style={{
                            fontSize: 11, color: "#92400E",
                            padding: "3px 0", borderBottom: "1px solid #FDE68A",
                          }}>
                            <span style={{
                              fontWeight: 600, fontFamily: "monospace",
                              background: "#FEF3C7", padding: "1px 4px",
                              borderRadius: 3, marginRight: 6,
                            }}>
                              [{alerte.id || "ISO"}]
                            </span>
                            {resolveTexte(alerte)}
                          </div>
                        ))}
                        {restantes > 0 && (
                          <div style={{ fontSize: 10, color: "#64748B", fontStyle: "italic", marginTop: 4 }}>
                            + {restantes} autre{restantes > 1 ? "s" : ""}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* BLOC 5 — Essais ISO 17892 recommandés (accordéon) */}
                  {diagnosticBackend?.essais?.iso17892_recommandes?.essais_prioritaires?.length > 0 && (
                    <div>
                      <button
                        onClick={() => setEssaisVisible(v => !v)}
                        style={{
                          width: "100%", padding: "8px 0",
                          background: "none", border: "none",
                          borderTop: "1px solid #E2E8F0",
                          textAlign: "left", cursor: "pointer",
                          fontSize: 11, fontWeight: 700, color: "#475569",
                        }}
                      >
                        🔬 Essais ISO 17892 recommandés ({diagnosticBackend.essais.iso17892_recommandes.nb_essais_total || 0})
                        {essaisVisible ? " ▲" : " ▼"}
                      </button>
                      {essaisVisible && (() => {
                        const { affichees, restantes } = limiterListe(
                          diagnosticBackend.essais.iso17892_recommandes.essais_prioritaires
                        );
                        return (
                          <div style={{ paddingTop: 8 }}>
                            {affichees.map((essai, i) => (
                              <div key={i} style={{
                                fontSize: 11, padding: "3px 0",
                                borderBottom: "1px solid #F1F5F9", color: "#475569",
                              }}>
                                <span style={{ fontWeight: 600, fontFamily: "monospace", marginRight: 6 }}>
                                  {essai.code}
                                </span>
                                {essai.nom}
                                {essai.obligatoire && (
                                  <span style={{ marginLeft: 6, fontSize: 10, color: "#DC2626", fontWeight: 600 }}>
                                    ● obligatoire
                                  </span>
                                )}
                              </div>
                            ))}
                            {restantes > 0 && (
                              <div style={{ fontSize: 10, color: "#64748B", fontStyle: "italic", marginTop: 4 }}>
                                + {restantes} autre{restantes > 1 ? "s" : ""}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                </div>
              </div>
            )}

            {/* ══ RAPPORT DIAGNOSTIC STRUCTURÉ — 3 SECTIONS ══ */}
            {(toutesAlertes.length > 0 || displayBest) && (
              <div style={{ marginBottom:36 }}>
                <div style={{ marginBottom:18 }}>
                  <div style={{ fontSize:12, color:GV.grisMid, fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>
                    Rapport d'Analyse Structuré
                  </div>
                </div>

                <div>

                {/* ── SECTION 1 : DIAGNOSTIC ── */}
                {toutesAlertes.length > 0 && (() => {
                  const alertesTri = [...toutesAlertes]
                    .sort((a,b) => {
                      const niv = { STRATEGIQUE:3, "OPÉRATIONNEL":2, VIGILANCE:1 };
                      return (niv[b.risque_niveau]||0) - (niv[a.risque_niveau]||0) || (b.priorite||0) - (a.priorite||0);
                    })
                    .slice(0, 3);
                  return (
                    <div style={{ border:"1.5px solid #FCA5A5", borderRadius:14, overflow:"hidden", marginBottom:16 }}>
                      <div style={{ background:"#fff", borderLeft:"5px solid #DC2626", padding:"10px 18px", display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontSize:15 }}>🔍</span>
                        <div>
                          <div style={{ fontSize:12, fontWeight:800, color:"#DC2626" }}>Section 1 — Analyse du Risque</div>
                          <div style={{ fontSize:11, color:"#9CA3AF" }}>
                            {alertesTri.length} point{alertesTri.length>1?"s":""} prioritaire{alertesTri.length>1?"s":""} · Détails complets → Analyse IA
                          </div>
                        </div>
                      </div>
                      <div style={{ background:"#FFF5F5" }}>
                        {alertesTri.map((a, i) => {
                          const priorite = a.priorite || 5;
                          const estStrategique = a.risque_niveau === "STRATEGIQUE";
                          const couleurBadge = estStrategique ? "#DC2626" : priorite >= 7 ? "#D97706" : "#2563EB";
                          const couleurBorder = estStrategique ? "#FCA5A5" : "#E5E7EB";
                          const labelNiveau = a.risque_niveau || (priorite >= 9 ? "CRITIQUE" : priorite >= 7 ? "ÉLEVÉ" : "MODÉRÉ");
                          const normeMatch = (a.alerte || a.justification || "").match(/(NF P\d+[-\d]*|EC[0-9]|RPA \d+|DTR[^·\n,]+|NF EN \d+[-\d]*)/);
                          const norme = normeMatch ? normeMatch[0].trim() : null;
                          return (
                            <div key={i} style={{ padding:"10px 14px", borderBottom: i < alertesTri.length-1 ? `1px solid ${couleurBorder}` : "none" }}>
                              <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                                <div style={{ background:couleurBadge, color:"#fff", borderRadius:6, padding:"2px 7px", fontSize:9, fontWeight:800, whiteSpace:"nowrap", flexShrink:0 }}>
                                  {labelNiveau}
                                </div>
                                {a.id && <span style={{ fontFamily:"monospace", fontSize:11, fontWeight:700, color:couleurBadge, background:`${couleurBadge}15`, padding:"2px 6px", borderRadius:4, flexShrink:0 }}>{a.id}</span>}
                                <span style={{ fontSize:12, fontWeight:700, color:"#1F2937", flex:1, minWidth:120 }}>
                                  {(a.alerte || "").replace(/^[⚠️🔴🟡ℹ️]+\s*/u,"").split("—")[0].trim()}
                                </span>
                                {norme && <span style={{ fontSize:10, color:"#6B7280", background:"#F3F4F6", borderRadius:4, padding:"2px 6px", flexShrink:0, whiteSpace:"nowrap" }}>{norme}</span>}
                              </div>
                              {a.modules && a.modules.length > 0 && (
                                <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:6, paddingLeft:2 }}>
                                  {a.modules.map(m => (
                                    <span key={m} style={{ background:"#EFF6FF", color:"#1D4ED8", borderRadius:99, padding:"2px 8px", fontSize:10, fontWeight:700 }}>{m}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {toutesAlertes.length > 3 && (
                          <div style={{ padding:"9px 20px", background:"#FEE2E2", fontSize:11, color:"#991B1B", fontStyle:"italic", borderTop:"1px solid #FCA5A5" }}>
                            + {toutesAlertes.length - 3} autre{toutesAlertes.length - 3 > 1 ? "s" : ""} règle{toutesAlertes.length - 3 > 1 ? "s" : ""} déclenchée{toutesAlertes.length - 3 > 1 ? "s" : ""} — consultez l'Analyse IA pour l'analyse complète
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* ── SECTION 2 : SOLUTION RECOMMANDÉE ── */}
                {displayBest && (
                  <div style={{ border:"2px solid #059669", borderRadius:14, overflow:"hidden", marginBottom:16 }}>
                    <div style={{ background:"#059669", padding:"12px 20px", display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:18 }}>✅</span>
                      <div>
                        <div style={{ fontSize:14, fontWeight:900, color:"#fff" }}>Section 2 — Solution Recommandée</div>
                        <div style={{ fontSize:11, color:"rgba(255,255,255,0.8)" }}>
                          {`Niveau d'adéquation : ${getLabelNiveau(displayBest)} · Critères multi-factoriels EC7 + EC8`}
                        </div>
                      </div>
                    </div>
                    <div style={{ background:"#F0FDF4", padding:"18px 20px" }}>
                      {/* En-tête solution */}
                      <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:16 }}>
                        <span style={{ fontSize:40 }}>{displayBest.icone || "🧠"}</span>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:18, fontWeight:900, color:"#064E3B" }}>{displayBest.nom}</div>
                          <div style={{ fontSize:12, color:"#065F46", marginTop:3 }}>
                            {donneesProjet?.hauteur && `H = ${donneesProjet.hauteur}m`}
                            {donneesProjet?.typeSol && ` · Sol : ${donneesProjet.typeSol}`}
                            {donneesProjet?.espaceDisponible && ` · Espace : ${donneesProjet.espaceDisponible}`}
                            {donneesProjet?.zoneSismique && ` · Zone sismique EC8 : ${donneesProjet.zoneSismique}`}
                          </div>
                        </div>
                        <div style={{ background:"#059669", borderRadius:10, padding:"10px 18px", textAlign:"center", flexShrink:0 }}>
                          <div style={{ fontSize:13, fontWeight:800, color:"#fff", padding:"4px 0" }}>{getLabelNiveau(displayBest)}</div>
                          <div style={{ fontSize:9, color:"rgba(255,255,255,0.8)", textTransform:"uppercase" }}>Adéquation</div>
                        </div>
                      </div>

                      {/* Argumentaire technique narratif */}
                      <div style={{ background:"#fff", borderRadius:8, padding:"14px 16px", border:"1px solid #A7F3D0", marginBottom:14 }}>
                        <div style={{ fontSize:11, fontWeight:800, color:"#059669", textTransform:"uppercase", letterSpacing:0.8, marginBottom:8 }}>
                          Argumentaire de choix
                        </div>
                        <div style={{ fontSize:13, color:"#064E3B", fontWeight:700, marginBottom:8 }}>
                          La solution {displayBest.nom} est retenue au terme de l'analyse comparative
                          {toutesAlertes.some(a => a.solutions_ecartees?.length > 0)
                            ? ` après élimination des alternatives incompatibles avec les contraintes du projet.`
                            : `.`}
                        </div>
                        <div style={{ fontSize:12, color:"#374151", lineHeight:1.8 }}>
                          {displayBest.avantages.map((av, i) => (
                            <div key={i} style={{ padding:"4px 0", borderBottom: i < displayBest.avantages.length-1 ? "1px solid #D1FAE5" : "none", display:"flex", gap:8 }}>
                              <span style={{ color:"#059669", flexShrink:0, fontWeight:700 }}>▸</span>
                              <span>{av}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Points de vigilance normatifs */}
                      {displayBest.inconvenients.length > 0 && (
                        <div style={{ background:"#FFFBEB", borderRadius:8, padding:"14px 16px", border:"1px solid #FDE68A", marginBottom:14 }}>
                          <div style={{ fontSize:11, fontWeight:800, color:"#B45309", textTransform:"uppercase", letterSpacing:0.8, marginBottom:8 }}>
                            Conditions de validité et points de vigilance
                          </div>
                          <div style={{ fontSize:12, color:"#92400E", lineHeight:1.8 }}>
                            {displayBest.inconvenients.map((inc, i) => (
                              <div key={i} style={{ padding:"4px 0", borderBottom: i < displayBest.inconvenients.length-1 ? "1px solid #FDE68A" : "none", display:"flex", gap:8 }}>
                                <span style={{ color:"#D97706", flexShrink:0, fontWeight:700 }}>⚠</span>
                                <span>{inc}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Modules de calcul réglementaires */}
                      <div style={{ paddingTop:4 }}>
                        <div style={{ fontSize:10, color:"#6B7280", fontWeight:700, textTransform:"uppercase", marginBottom:6 }}>
                          Méthode de justification réglementaire (EC7 DA2*)
                        </div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                          {getModulesGEO5(displayBest.id, diagnosticBackend, displayBest).map(m => (
                            <span key={m} style={{ background:"#059669", color:"#fff", borderRadius:99, padding:"3px 12px", fontSize:11, fontWeight:700 }}>
                              Module {m}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── SECTION EC7 : INDICATEURS PRÉ-DIMENSIONNEMENT ── */}
                {preDimData && donneesProjet?.typeOuvrage === "fondation_batiment" && (() => {
                  const c = preDimData.calculs || {};
                  const syn = preDimData.synthese || {};
                  const EC7_CHECKS = [
                    { k:"ec7_01_portance",    label:"EC7-01 Portance ELU" },
                    { k:"ec7_02_glissement",  label:"EC7-02 Glissement ELU" },
                    { k:"ec7_03_excentricite",label:"EC7-03 Excentricité" },
                    { k:"ec7_04_tassement",   label:"EC7-04 Tassement ELS" },
                    { k:"ec7_05_stabilite",   label:"EC7-05 Stabilité globale" },
                  ].filter(({ k }) => c[k]);
                  return (
                    <div style={{ border:"1px solid #F59E0B", borderRadius:14, overflow:"hidden", marginBottom:16 }}>
                      <div style={{ background:"#FFFBEB", borderBottom:"1px solid #FDE68A", padding:"10px 18px", display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontSize:15 }}>📐</span>
                        <div>
                          <div style={{ fontSize:12, fontWeight:800, color:"#92400E" }}>Indicateurs EC7 DA2* — Pré-dimensionnement</div>
                          <div style={{ fontSize:11, color:"#B45309" }}>
                            {syn.verifications_conformes} vérifications conformes · tassement estimé {syn.tassement_total_mm} mm
                          </div>
                        </div>
                        <div style={{ marginLeft:"auto", background:"#FEF3C7", border:"1px solid #FCD34D", borderRadius:6, padding:"2px 8px" }}>
                          <span style={{ fontSize:10, fontWeight:700, color:"#92400E" }}>INDICATIF</span>
                        </div>
                      </div>
                      <div style={{ background:"#FFFBEB", padding:"14px 20px" }}>
                        {/* Tableau vérifications */}
                        <div style={{ display:"grid", gridTemplateColumns:"1fr auto auto", gap:0, marginBottom:12 }}>
                          {EC7_CHECKS.map(({ k, label }) => {
                            const v = c[k];
                            const ok = v?.conforme !== false;
                            return (
                              <div key={k} style={{ display:"contents" }}>
                                <div style={{ padding:"6px 10px", fontSize:12, fontWeight:600, color:"#1C1917", borderBottom:"1px solid #FED7AA" }}>{label}</div>
                                <div style={{ padding:"6px 10px", fontSize:11, color:"#78716c", borderBottom:"1px solid #FED7AA", whiteSpace:"nowrap" }}>{v?.norme}</div>
                                <div style={{ padding:"6px 10px", borderBottom:"1px solid #FED7AA", textAlign:"right" }}>
                                  <span style={{ background: ok ? "#DCFCE7" : "#FEE2E2", color: ok ? "#166534" : "#991B1B", borderRadius:4, padding:"2px 8px", fontSize:10, fontWeight:800 }}>
                                    {ok ? "✓" : "✗"} {v?.verdict}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* Portance + Tassement résumé */}
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                          {c.portance_terzaghi && (
                            <div style={{ background:"#fff", borderRadius:8, padding:"10px 12px", border:"1px solid #FED7AA" }}>
                              <div style={{ fontSize:10, color:"#92400E", fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>Portance de calcul (Terzaghi)</div>
                              <div style={{ fontSize:20, fontWeight:900, color:"#EA580C" }}>{c.portance_terzaghi.qd_kPa} kPa</div>
                              <div style={{ fontSize:10, color:"#6B7280" }}>qu={c.portance_terzaghi.qu_kPa} kPa · γR={c.portance_terzaghi.gamma_R}</div>
                            </div>
                          )}
                          <div style={{ background:"#fff", borderRadius:8, padding:"10px 12px", border:"1px solid #FED7AA" }}>
                            <div style={{ fontSize:10, color:"#92400E", fontWeight:700, textTransform:"uppercase", marginBottom:4 }}>Tassement total estimé</div>
                            <div style={{ fontSize:20, fontWeight:900, color: syn.tassement_total_mm > 25 ? "#DC2626" : "#EA580C" }}>
                              {syn.tassement_total_mm} mm
                            </div>
                            <div style={{ fontSize:10, color:"#6B7280" }}>
                              immédiat + consolidation · admissible 25 mm (béton armé)
                            </div>
                          </div>
                        </div>
                        {/* Disclaimer obligatoire */}
                        <div style={{ background:"#FFEDD5", border:"1px solid #FB923C", borderRadius:8, padding:"10px 14px", fontSize:11, color:"#7C2D12", fontStyle:"italic" }}>
                          ⚠ Valeur indicative — G2 PRO requis · {preDimData.disclaimer}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* ── SECTION 3 : SYNTHÈSE DES RECOMMANDATIONS TECHNIQUES ── */}
                {displayBest && (() => {
                  // Collecter tous les effets domino uniques de toutes les alertes
                  const dominoSet = new Set();
                  toutesAlertes.forEach(a => (a.effets_domino || []).forEach(d => dominoSet.add(d)));
                  const dominos = [...dominoSet];

                  // Vérifications normatives clés à partir des alertes actives
                  const vigilancesNormatives = [];
                  toutesAlertes.forEach(a => {
                    (a.solutions_ecartees || []).forEach(s => {
                      if (s.raison_normative && !vigilancesNormatives.includes(s.raison_normative)) {
                        vigilancesNormatives.push(s.raison_normative);
                      }
                    });
                  });

                  return (
                    <div style={{ border:"2px solid #1D4ED8", borderRadius:14, overflow:"hidden" }}>
                      <div style={{ background:"#1D4ED8", padding:"12px 20px", display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontSize:18 }}>📋</span>
                        <div>
                          <div style={{ fontSize:14, fontWeight:900, color:"#fff" }}>Section 3 — Synthèse des Recommandations Techniques</div>
                          <div style={{ fontSize:11, color:"rgba(255,255,255,0.8)" }}>
                            Note de synthèse réglementaire · EC7 DA2* + NF P94 + EC8
                          </div>
                        </div>
                      </div>
                      <div style={{ background:"#EFF6FF", padding:"18px 20px" }}>

                        {/* Conclusion normative narrative */}
                        <div style={{ background:"#fff", borderRadius:8, padding:"14px 16px", border:"1px solid #BFDBFE", marginBottom:16 }}>
                          <div style={{ fontSize:11, fontWeight:800, color:"#1D4ED8", textTransform:"uppercase", letterSpacing:0.8, marginBottom:10 }}>
                            Conclusion de l'analyse de faisabilité
                          </div>
                          <div style={{ fontSize:12, color:"#1E3A8A", lineHeight:1.9 }}>
                            {toutesAlertes.some(a => a.risque_niveau === "SYSTEMIQUE") && (
                              <p style={{ marginBottom:10 }}>
                                L'analyse a identifié une interaction sol-structure de niveau <strong>SYSTEMIQUE</strong> qui dicte l'ensemble des choix structuraux. Les calculs isolés de chaque ouvrage sont invalides dans cette configuration — seule une approche de système couplé répond aux exigences réglementaires.
                              </p>
                            )}
                            <p style={{ marginBottom:10 }}>
                              La solution <strong>{displayBest.nom}</strong> est la seule réponse technique recevable au regard des contraintes cumulées du projet
                              {donneesProjet?.hauteur ? ` (H = ${donneesProjet.hauteur} m` : ""}
                              {donneesProjet?.zoneSismique ? `, Zone sismique EC8-${donneesProjet.zoneSismique}` : ""}
                              {donneesProjet?.hauteur || donneesProjet?.zoneSismique ? ")." : "."}
                            </p>
                            {vigilancesNormatives.length > 0 && (
                              <p style={{ marginBottom:10 }}>
                                La justification du dossier devra couvrir simultanément les exigences suivantes : {vigilancesNormatives.slice(0,3).join(" ; ")}.
                              </p>
                            )}
                            {dominos.length > 0 && (
                              <p style={{ marginBottom:0 }}>
                                Ce choix entraîne les vérifications en cascade obligatoires : <strong>{dominos.map(d => d.replace(/_/g," ")).join(", ")}</strong>. Ces analyses inter-domaines ne peuvent être dissociées du dimensionnement principal.
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Références normatives consolidées — 3 max */}
                        {vigilancesNormatives.length > 0 && (
                          <div style={{ marginBottom:16 }}>
                            <div style={{ fontSize:11, fontWeight:800, color:"#1D4ED8", textTransform:"uppercase", letterSpacing:0.8, marginBottom:8 }}>
                              Références normatives applicables
                            </div>
                            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                              {vigilancesNormatives.slice(0, 3).map((ref, i) => (
                                <div key={i} style={{ background:"#EFF6FF", borderRadius:6, padding:"7px 12px", border:"1px solid #BFDBFE", fontSize:11, color:"#1E40AF", display:"flex", gap:8, alignItems:"flex-start" }}>
                                  <span style={{ color:"#1D4ED8", flexShrink:0, fontWeight:700 }}>§</span>
                                  <span>{ref}</span>
                                </div>
                              ))}
                              {vigilancesNormatives.length > 3 && (
                                <div style={{ fontSize:10, color:"#6B7280", fontStyle:"italic", paddingLeft:4 }}>
                                  + {vigilancesNormatives.length - 3} autre{vigilancesNormatives.length - 3 > 1 ? "s" : ""} référence{vigilancesNormatives.length - 3 > 1 ? "s" : ""} — voir Analyse IA
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Vérifications en cascade — masquées par défaut */}
                        {dominos.length > 0 && (
                          <div style={{ marginBottom: missionsVisibles.length > 0 ? 12 : 0 }}>
                            <button
                              onClick={() => setVerif3Visible(v => !v)}
                              style={{ background:"none", border:"none", cursor:"pointer", padding:0, display:"flex", alignItems:"center", gap:6, marginBottom:8 }}
                            >
                              <span style={{ fontSize:11, fontWeight:800, color:"#7C3AED", textTransform:"uppercase", letterSpacing:0.8 }}>
                                {verif3Visible ? "▲" : "▼"} Vérifications en cascade ({dominos.length})
                              </span>
                            </button>
                            {verif3Visible && (
                              <div style={{ display:"flex", flexDirection:"column", gap:5, animation:"fadeIn 0.3s ease" }}>
                                {dominos.map((d, i) => (
                                  <div key={i} style={{ background:"#FAF5FF", borderRadius:6, padding:"7px 12px", border:"1px solid #DDD6FE", fontSize:12, color:"#4C1D95", display:"flex", gap:8 }}>
                                    <span style={{ color:"#7C3AED", flexShrink:0, fontWeight:700 }}>→</span>
                                    <span style={{ fontWeight:600 }}>{d.replace(/_/g," ")}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Missions géotechniques EN 1997-2 */}
                        {/* ═══ LEGACY FALLBACK — Phase 8D-B1 ═══ */}
                        {/* Source : backend.alertes.missions en BACKEND, missionsEN2 frontend en FALLBACK (R9) */}
                        {missionsVisibles.length > 0 && (
                          <div style={{ marginTop: dominos.length > 0 ? 0 : undefined }}>
                            <div style={{ fontSize:11, fontWeight:800, color:"#92400E", textTransform:"uppercase", letterSpacing:0.8, marginBottom:8 }}>
                              Missions géotechniques recommandées (EN 1997-2)
                            </div>
                            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                              {missionsVisibles.map((m, i) => (
                                <div key={i} style={{ background:"#FEF3C7", borderRadius:6, padding:"8px 12px", border:"1px solid #FCD34D", fontSize:12, color:"#78350F", display:"flex", gap:8, alignItems:"center" }}>
                                  <span style={{ flexShrink:0 }}>📋</span>
                                  <span style={{ fontWeight:700 }}>{typeof m === "string" ? m : (m?.mission || m?.label || resolveTexte(m))}</span>
                                  <span style={{ color:"#92400E", fontWeight:400 }}>— NF P94-500 + EN 1997-2 §2.2.1</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                </div>
              </div>
            )}

            {/* ── OUVRIR RAPPORT ── */}
            <div style={{ display:"flex", justifyContent:"center", margin:"24px 0" }}>
              <button
                onClick={onRapport}
                style={{
                  background: "#2D7A2D",
                  color: "#ffffff",
                  border: "2px solid #2D7A2D",
                  borderRadius: 10,
                  padding: "12px 28px",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                📄 Ouvrir le rapport
              </button>
            </div>

            {/* ── ACTIONS ── */}
            <div style={{
              background:GV.gris, borderRadius:14, padding:"24px 28px",
              display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:16
            }}>
              <div>
                <div style={{ fontSize:15, fontWeight:800, color:GV.dark, marginBottom:4 }}>
                  {t("solution_page.prochaine_etape_titre")}
                </div>
                <div style={{ fontSize:13, color:GV.grisMid }}>
                  {t("solution_page.prochaine_etape_desc")}
                </div>
              </div>
              <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                <button onClick={onRetour} style={{
                  background:GV.blanc, border:`1.5px solid ${GV.border}`,
                  borderRadius:8, padding:"12px 24px", fontSize:14, fontWeight:600,
                  cursor:"pointer", color:GV.dark
                }}>
                  {t("solution_page.retour_accueil")}
                </button>
                <button onClick={onAgent} style={{
                  background:`linear-gradient(135deg, ${GV.vert}, ${GV.vertClair})`,
                  border:"none", borderRadius:8, padding:"12px 28px",
                  fontSize:14, fontWeight:800, cursor:"pointer", color:GV.blanc,
                  boxShadow:`0 4px 16px ${GV.vert}40`
                }}>
                  {t("solution_page.guider_agent")}
                </button>
              </div>
            </div>
          </>
        )}
      </div>



      {/* ── Modal Enregistrer sous ────────────────────────────────────────── */}
      {showSaveAsModal && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setShowSaveAsModal(false); }}
          style={{
            position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000,
            display:"flex", alignItems:"center", justifyContent:"center",
          }}
        >
          <div style={{
            background:"#fff", borderRadius:12, padding:28, width:420, maxWidth:"90vw",
            boxShadow:"0 8px 32px rgba(0,0,0,0.18)",
          }}>
            <h3 style={{ margin:"0 0 20px", fontSize:16, fontWeight:800, color:GV.dark }}>
              Enregistrer sous
            </h3>

            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, color:GV.grisMid, fontWeight:700, marginBottom:6 }}>
                NOM / ENTREPRISE
              </div>
              <input
                type="text"
                value={saveAsNomEntreprise}
                onChange={e => setSaveAsNomEntreprise(e.target.value)}
                style={{
                  width:"100%", boxSizing:"border-box",
                  border:`1px solid ${GV.border}`, borderRadius:7,
                  padding:"8px 10px", fontSize:13, color:GV.dark, outline:"none",
                }}
              />
            </div>

            <div style={{ marginBottom:22 }}>
              <div style={{ fontSize:11, color:GV.grisMid, fontWeight:700, marginBottom:6 }}>
                RÉFÉRENCE PROJET
              </div>
              <input
                type="text"
                value={saveAsCodeRef}
                onChange={e => setSaveAsCodeRef(e.target.value)}
                style={{
                  width:"100%", boxSizing:"border-box",
                  border:`1px solid ${GV.border}`, borderRadius:7,
                  padding:"8px 10px", fontSize:13, color:GV.dark, outline:"none",
                }}
              />
            </div>

            <div style={{ display:"flex", justifyContent:"flex-end", gap:10 }}>
              <button
                onClick={() => setShowSaveAsModal(false)}
                style={{
                  background:"#fff", border:`1px solid ${GV.border}`, color:GV.grisMid,
                  borderRadius:7, padding:"8px 18px", cursor:"pointer", fontSize:13, fontWeight:600,
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleEnregistrerSous}
                style={{
                  background:GV.vert, border:"none", color:"#fff",
                  borderRadius:7, padding:"8px 18px", cursor:"pointer", fontSize:13, fontWeight:700,
                }}
              >
                Créer la copie
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


// =============================================================================
// PAGE DÉMARRER LE PROJET — même charte que Présentation
// L'ingénieur décrit son projet → l'IA propose des solutions géotechniques
// La comparaison porte sur le COÛT DE L'OUVRAGE, pas sur les modules GEO5
// =============================================================================


export { AlerteModulesManquants, PageSolution };
